import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatUkDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  BookOpen,
  Building2,
  CalendarClock,
  CheckCircle2,
  HelpCircle,
  KeyRound,
  Percent,
  PlugZap,
  RefreshCw,
  Save,
  Search,
  Store,
  Trash2,
  Upload,
  UsersRound,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  DEFAULT_PAGE_SIZE,
  PaginationFooter,
  normalisePaginatedResponse,
  normalisePageSize,
} from "./accountancy-software/shared";
import {
  clearCachedAccountingWorkspace,
  fetchAccountingWorkspace,
  updateCachedAccountingClient,
} from "./accountancy-software/accountingWorkspaceCache";

const DEFAULT_CLIENT_TYPES = [
  ["limited_company", "Limited company"],
  ["sole_trader", "Sole trader"],
  ["partnership", "Partnership"],
  ["llp", "LLP"],
  ["charity", "Charity"],
  ["community_interest_company", "CIC"],
  ["club_or_association", "Club / association"],
  ["cis_customer", "CIS customer"],
  ["landlord", "Landlord"],
  ["individual", "Individual"],
  ["trust_or_estate", "Trust / estate"],
  ["other", "Other"],
];

const INDUSTRIES = [
  "Accommodation and food services",
  "Administrative and support services",
  "Agriculture, forestry and fishing",
  "Arts, entertainment and recreation",
  "Construction",
  "Education",
  "Financial and insurance activities",
  "Health and social work",
  "Information and communication",
  "Manufacturing",
  "Motor trade",
  "Professional, scientific and technical",
  "Property and real estate",
  "Retail and wholesale",
  "Transport and storage",
  "Utilities",
  "Other service activities",
];

const CLIENT_FIELD_HELP = {
  "Client type": "Select the client's legal or tax form. This controls the suggested services and year-end filing workflow; charities, clubs and other ambiguous forms still require a legal-classification review.",
  Industry: "The client's main trade or activity. It supports risk review, account-code suggestions and reporting but does not replace the legal client type.",
  "Practice manager": "The person in the practice responsible for this client. This is an internal ownership field and is not submitted to an authority.",
  Status: "Active clients can use enabled workflows. Inactive clients remain retained for history but should not receive new routine processing.",
  "First name": "Given name of the client's main contact or individual taxpayer.",
  "Last name": "Family name of the client's main contact or individual taxpayer.",
  "Business name": "The trading or display name used across the practice workspace. Enter the legal name separately where the filing workflow asks for it.",
  "Login email": "Email used by the client to sign in. It must be unique and should belong to the authorised client user.",
  Phone: "Primary contact telephone number for practice communication.",
  "Email for purchase invoices": "Dedicated address used to receive purchase invoices and bills into Submitted Items.",
  "Email for sales invoices (optional)": "Optional dedicated address used to receive sales invoices and credit notes into Submitted Items.",
  "Year end": "The client's accounting reference or reporting year end. Confirm it against Companies House or the applicable tax basis before generating deadlines.",
  "VAT client": "Enable only while the client is VAT registered for the relevant period. VAT codes remain viewable when disabled, but VAT returns and VAT treatment must not be generated.",
  "AI analysis": "Allows uploaded documents to be analysed before review. Suggestions must still be checked by an authorised user before posting.",
  UTR: "The HMRC Unique Taxpayer Reference for the relevant taxpayer or entity. Treat it as confidential and validate its ownership before filing.",
  "VAT number": "The client's VAT registration number. It is required for VAT workflows and must match the registered entity.",
  "PAYE reference": "The employer PAYE reference issued by HMRC, normally in office/reference format. It is required only for employer PAYE workflows.",
  "Accounts office ref": "The 13-character HMRC Accounts Office reference used for PAYE payments and employer submissions.",
  "Statutory submission route": "External/manual records a filing completed outside EPOS and never transmits data. Direct submission saves internally first and then uses a configured, certified authority transport; it fails safely if that transport is unavailable.",
  "Authorisation codes / access notes": "Restricted practice notes for authority access and client authorisation. Do not place reusable secrets here when a protected integration credential field is available.",
};

function helpFor(label) {
  return CLIENT_FIELD_HELP[label] || `${label} affects this client's accountancy setup. Review the value and its downstream workflow before saving.`;
}

const DEFAULT_SERVICES = [
  { key: "accounts", label: "Accounts", deadline: "statutory", recurrence: null, start_date: null, statutory_key: "companies_house_accounts_due" },
  { key: "bookkeeping", label: "Bookkeeping", deadline: null, recurrence: null, start_date: null },
  { key: "ct600_return", label: "CT600 Return", deadline: "statutory", recurrence: null, start_date: null, statutory_key: "hmrc_ct600_filing_due" },
  { key: "partnership_return", label: "Partnership Tax Return (SA800)", deadline: "statutory", recurrence: null, start_date: null, statutory_key: "hmrc_partnership_return_due" },
  { key: "corporation_tax_payment", label: "Corporation Tax Payment", deadline: "statutory", recurrence: null, start_date: null, statutory_key: "hmrc_corporation_tax_payment_due" },
  { key: "payroll", label: "Payroll", deadline: "scheduled", recurrence: "monthly", start_date: null },
  { key: "auto_enrolment", label: "Auto-Enrolment", deadline: "scheduled", recurrence: "annual", start_date: null },
  { key: "vat_returns", label: "VAT Returns", deadline: "statutory", recurrence: null, start_date: null, statutory_key: "hmrc_vat_return_due" },
  { key: "management_accounts", label: "Management Accounts", deadline: "scheduled", recurrence: "monthly", start_date: null },
  { key: "confirmation_statement", label: "Confirmation Statement", deadline: "statutory", recurrence: null, start_date: null, statutory_key: "companies_house_confirmation_due" },
  { key: "cis", label: "CIS", deadline: "scheduled", recurrence: "monthly", start_date: null },
  { key: "p11d", label: "P11D", deadline: "scheduled", recurrence: "annual", start_date: null },
  { key: "fee_protection", label: "Fee Protection Service", deadline: null, recurrence: null, start_date: null },
  { key: "registered_address", label: "Registered Address", deadline: null, recurrence: null, start_date: null },
  { key: "bill_payment", label: "Bill Payment", deadline: null, recurrence: null, start_date: null },
  { key: "consultation_advice", label: "Consultation/Advice", deadline: null, recurrence: null, start_date: null },
  { key: "software", label: "Software", deadline: null, recurrence: null, start_date: null },
  { key: "ct600e", label: "CT600E", deadline: "scheduled", recurrence: "annual", start_date: null },
  { key: "cic34_report", label: "CIC annual report (CIC34)", deadline: "statutory", recurrence: null, start_date: null, statutory_key: "companies_house_accounts_due" },
  { key: "charity_annual_return", label: "Charity annual return", deadline: "scheduled", recurrence: "annual", start_date: null },
  { key: "self_assessment", label: "Self Assessment", deadline: "scheduled", recurrence: "annual", start_date: null },
  { key: "self_assessment_payment", label: "Self Assessment Payment", deadline: "scheduled", recurrence: "annual", start_date: null },
  { key: "payment_on_account", label: "Payment on Account", deadline: "scheduled", recurrence: "annual", start_date: null },
];

const COMBINED_PRICING = [
  { key: "annual_charge", label: "Annual Charge" },
  { key: "monthly_charge", label: "Monthly Charge" },
];

const CLIENT_MODULES = [
  {
    key: "accountancy",
    label: "Accountancy",
    description: "Enable the accountancy workspace, client accountancy settings, and software integration flow.",
    accent: "emerald",
  },
  {
    key: "payroll",
    label: "Payroll",
    description: "Add this client to the payroll employer list and provision their payroll workspace.",
    accent: "sky",
  },
  {
    key: "epos_bookings",
    label: "EPOS Bookings",
    description: "Reserve access to the EPOS booking system. The connected workflow will be added later.",
    accent: "amber",
    comingSoon: true,
  },
];

const PRACTICE_FIELDS = [
  "client_type",
  "statutory_submission_route",
  "industry",
  "company_number",
  "company_status",
  "incorporation_date",
  "registered_office_address",
  "trading_address",
  "phone",
  "utr",
  "vat_number",
  "paye_reference",
  "accounts_office_reference",
  "authorisation_codes",
  "services_required",
  "service_settings",
  "module_settings",
  "statutory_deadlines",
  "deadline_tasks",
  "bookkeeping_frequency",
  "payroll_frequency",
  "year_end",
  "practice_manager",
  "companies_house_last_checked",
  "main_contact_name",
  "main_contact_role",
  "company_directors",
  "company_pscs",
  "company_contacts",
  "companies_house_filings",
];

const INTEGRATION_PROVIDERS = [
  { value: "quickbooks", label: "QuickBooks" },
  { value: "sage", label: "Sage" },
  { value: "xero", label: "Xero" },
];

const INTEGRATION_RECORD_TABS = [
  { key: "account", label: "Chart of Accounts", icon: BookOpen, empty: "No account codes synced or added yet." },
  { key: "supplier", label: "Supplier List", icon: Store, empty: "No suppliers synced or added yet." },
  { key: "customer", label: "Customer List", icon: UsersRound, empty: "No customers synced or added yet." },
  { key: "tax_code", label: "VAT Codes", icon: Percent, empty: "No VAT codes synced or added yet." },
];

const NATIVE_RECORD_TABS = [
  { key: "account", label: "Native Chart of Accounts", icon: BookOpen, empty: "No native chart of accounts records yet." },
  { key: "supplier", label: "AP Suppliers", icon: Store, empty: "No AP suppliers created yet." },
  { key: "customer", label: "AR Customers", icon: UsersRound, empty: "No AR customers created yet." },
  { key: "tax_code", label: "Native VAT Codes", icon: Percent, empty: "No native VAT codes configured yet." },
  { key: "bank_account", label: "Native Bank Accounts", icon: Building2, empty: "No native bank accounts configured yet." },
];

const DEFAULT_INTEGRATION_SETTINGS = {
  provider: "quickbooks",
  status: "not_connected",
  company_id: "",
  company_name: "",
  sandbox: false,
  auto_create_suppliers: true,
  auto_create_customers: true,
  default_purchase_account: "",
  default_sales_account: "",
  default_vat_code: "",
  notes: "",
};

export default function AdminClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [items, setItems] = useState({ purchase: [], sales: [] });
  const [pageTab, setPageTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get("section");
    if (requested === "accountancy") return "services";
    return ["details", "services", "software", "deadlines", "items"].includes(requested) ? requested : "details";
  });
  const [itemsTab, setItemsTab] = useState("purchase");
  const [pwd, setPwd] = useState("");
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [companyBusy, setCompanyBusy] = useState(false);
  const [integrationDetail, setIntegrationDetail] = useState({ integration: null, records: {}, counts: {} });
  const [nativeAccountingDetail, setNativeAccountingDetail] = useState(null);
  const [integrationSettings, setIntegrationSettings] = useState(DEFAULT_INTEGRATION_SETTINGS);
  const [integrationTab, setIntegrationTab] = useState("account");
  const [savedAccountingDestination, setSavedAccountingDestination] = useState("");
  const [savedVatClient, setSavedVatClient] = useState(false);
  const [providerConfigs, setProviderConfigs] = useState({});
  const [integrationBusy, setIntegrationBusy] = useState(false);
  const [outstandingLoaded, setOutstandingLoaded] = useState(false);
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [accountancySoftwareLoaded, setAccountancySoftwareLoaded] = useState(false);
  const [accountancySoftwareLoading, setAccountancySoftwareLoading] = useState(false);
  const [externalIntegrationLoaded, setExternalIntegrationLoaded] = useState(false);
  const [providerConfigsLoading, setProviderConfigsLoading] = useState(false);
  const [serviceCatalog, setServiceCatalog] = useState(DEFAULT_SERVICES);
  const [clientTypes, setClientTypes] = useState(DEFAULT_CLIENT_TYPES.map(([key, label]) => ({ key, label, service_keys: [] })));

  const load = useCallback(async () => {
    try {
      const [c, services, accountancyClientTypes] = await Promise.all([
        api.get(`/admin/clients/${id}`),
        api.get("/admin/accountancy/services").catch(() => ({ data: { services: DEFAULT_SERVICES } })),
        api.get("/admin/accountancy/client-types").catch(() => ({ data: { client_types: DEFAULT_CLIENT_TYPES.map(([key, label]) => ({ key, label, service_keys: [] })) } })),
      ]);
      setClient(c.data);
      setSavedVatClient(!!c.data?.is_vat_client);
      setSavedAccountingDestination(accountingDestinationForClient(c.data));
      setCompanyQuery(c.data?.business_name || "");
      const nextServices = Array.isArray(services.data.services) && services.data.services.length ? services.data.services : DEFAULT_SERVICES;
      const configuredClientTypes = Array.isArray(accountancyClientTypes.data.client_types)
        ? accountancyClientTypes.data.client_types
        : [];
      const configuredClientTypeKeys = new Set(configuredClientTypes.map((type) => type.key));
      const nextClientTypes = [
        ...configuredClientTypes,
        ...DEFAULT_CLIENT_TYPES
          .filter(([key]) => !configuredClientTypeKeys.has(key))
          .map(([key, label]) => ({ key, label, service_keys: [] })),
      ];
      setServiceCatalog(nextServices.filter((service) => service.enabled !== false));
      setClientTypes(nextClientTypes);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);

  useEffect(() => {
    setItems({ purchase: [], sales: [] });
    setOutstandingLoaded(false);
    setOutstandingLoading(false);
    setIntegrationDetail({ integration: null, records: {}, counts: {} });
    setNativeAccountingDetail(null);
    setIntegrationSettings(DEFAULT_INTEGRATION_SETTINGS);
    setIntegrationTab("account");
    setProviderConfigs({});
    setAccountancySoftwareLoaded(false);
    setAccountancySoftwareLoading(false);
    setExternalIntegrationLoaded(false);
    setProviderConfigsLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const loadProviderConfigs = useCallback(async ({ force = false } = {}) => {
    if (providerConfigsLoading || (externalIntegrationLoaded && !force)) return;
    setProviderConfigsLoading(true);
    try {
      const responses = await Promise.all(
        INTEGRATION_PROVIDERS.map(({ value }) => api.get(`/admin/integrations/${value}/config`))
      );
      setProviderConfigs(Object.fromEntries(
        responses.map(({ data }, index) => [INTEGRATION_PROVIDERS[index].value, data])
      ));
      setExternalIntegrationLoaded(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProviderConfigsLoading(false);
    }
  }, [externalIntegrationLoaded, providerConfigsLoading]);

  const loadOutstandingItems = useCallback(async ({ force = false } = {}) => {
    if (outstandingLoading || (outstandingLoaded && !force)) return;
    setOutstandingLoading(true);
    try {
      const [purchase, sales] = await Promise.all([
        api.get(`/admin/clients/${id}/items`, { params: { type: "purchase" } }),
        api.get(`/admin/clients/${id}/items`, { params: { type: "sales" } }),
      ]);
      setItems({ purchase: purchase.data || [], sales: sales.data || [] });
      setOutstandingLoaded(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOutstandingLoading(false);
    }
  }, [id, outstandingLoaded, outstandingLoading]);

  const loadAccountancySoftware = useCallback(async ({ force = false } = {}) => {
    if (accountancySoftwareLoading || (accountancySoftwareLoaded && !force)) return;
    setAccountancySoftwareLoading(true);
    try {
      const [integration, nativeAccounting] = await Promise.all([
        api.get(`/admin/integrations/clients/${id}`),
        api.get(`/admin/accounting/clients/${id}`).catch(() => ({ data: null })),
      ]);
      setIntegrationDetail(integration.data);
      setNativeAccountingDetail(nativeAccounting.data);
      setIntegrationSettings({ ...DEFAULT_INTEGRATION_SETTINGS, ...(integration.data.integration || {}) });
      setAccountancySoftwareLoaded(true);
      if (accountingDestinationForClient(client || {}) === "external") {
        await loadProviderConfigs({ force });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAccountancySoftwareLoading(false);
    }
  }, [accountancySoftwareLoaded, accountancySoftwareLoading, client, id, loadProviderConfigs]);

  useEffect(() => {
    if (pageTab === "items") loadOutstandingItems();
    if (pageTab === "software") loadAccountancySoftware();
  }, [loadAccountancySoftware, loadOutstandingItems, pageTab]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (pageTab === "details") params.delete("section");
    else params.set("section", pageTab);
    const query = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  }, [pageTab]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const quickbooks = params.get("quickbooks");
    const provider = params.get("provider");
    const connection = params.get("connection");
    const sync = params.get("sync");
    const connectedProvider = provider || (quickbooks ? "quickbooks" : "");
    const connectionStatus = connection || quickbooks;
    if (connectionStatus === "connected" && sync === "ok") {
      toast.success("Accountancy software connected and lists synced");
      setPageTab("software");
      loadAccountancySoftware({ force: true });
    } else if (connectionStatus === "connected" && sync === "error") {
      toast.warning(params.get("message") || "Connected, but list sync needs another try");
      setPageTab("software");
      loadAccountancySoftware({ force: true });
    } else if (connectionStatus === "error") {
      toast.error(params.get("message") || `${providerLabel(connectedProvider)} connection failed`);
    } else if (connectionStatus === "missing") {
      toast.error("Connection callback was missing required details");
    }
    if (quickbooks || provider || connection) {
      const cleanParams = new URLSearchParams(window.location.search);
      ["quickbooks", "provider", "connection", "sync", "message"].forEach((key) => cleanParams.delete(key));
      cleanParams.set("section", "software");
      const query = cleanParams.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    }
  }, [loadAccountancySoftware]);

  const clientTypeOptions = useMemo(
    () => (clientTypes.length ? clientTypes : DEFAULT_CLIENT_TYPES.map(([key, label]) => ({ key, label }))).map((type) => [type.key, type.label]),
    [clientTypes]
  );
  const availableServices = useMemo(() => {
    const type = clientTypes.find((item) => item.key === client?.client_type);
    if (!type || !Array.isArray(type.service_keys) || type.service_keys.length === 0) return serviceCatalog;
    const allowed = new Set(type.service_keys);
    return serviceCatalog.filter((service) => allowed.has(service.key));
  }, [clientTypes, client?.client_type, serviceCatalog]);
  const selectedServices = useMemo(
    () => enabledServiceLabels(availableServices, client?.service_settings, client?.services_required),
    [availableServices, client?.service_settings, client?.services_required]
  );
  const serviceSettings = useMemo(() => normaliseServiceSettings(availableServices, client?.service_settings, client?.services_required), [availableServices, client?.service_settings, client?.services_required]);
  const moduleSettings = useMemo(() => normaliseModuleSettings(client?.module_settings), [client?.module_settings]);
  const accountancyModuleEnabled = !!moduleSettings.accountancy.enabled;
  const deadlineTasks = useMemo(() => parseStoredList(client?.deadline_tasks), [client?.deadline_tasks]);
  const directors = useMemo(() => parseStoredList(client?.company_directors), [client?.company_directors]);
  const pscs = useMemo(() => parseStoredList(client?.company_pscs), [client?.company_pscs]);
  const contacts = useMemo(() => parseStoredList(client?.company_contacts), [client?.company_contacts]);
  const filings = useMemo(() => parseStoredList(client?.companies_house_filings), [client?.companies_house_filings]);

  useEffect(() => {
    if (client && !accountancyModuleEnabled && pageTab === "software") {
      setPageTab("services");
    }
  }, [accountancyModuleEnabled, client, pageTab]);

  function setField(key, value) {
    setClient((current) => ({ ...current, [key]: value }));
  }

  function updateModuleSettings(next) {
    setField("module_settings", JSON.stringify(next));
  }

  function toggleModule(moduleKey) {
    const next = normaliseModuleSettings(client?.module_settings);
    next[moduleKey] = { ...next[moduleKey], enabled: !next[moduleKey].enabled };
    updateModuleSettings(next);
  }

  function updateModuleStartDate(moduleKey, startDate) {
    const next = normaliseModuleSettings(client?.module_settings);
    next[moduleKey] = { ...next[moduleKey], start_date: startDate };
    updateModuleSettings(next);
  }

  async function saveClient() {
    try {
      const vatChanged = savedVatClient !== !!client.is_vat_client;
      const nextDestination = accountingDestinationForClient(client);
      const destinationChanged = savedAccountingDestination && savedAccountingDestination !== nextDestination;
      if (destinationChanged && destinationChangeMayAffectPublishedDocuments(client)) {
        const confirmed = window.confirm("Changing accounting destination could affect published documents. Save this destination change?");
        if (!confirmed) return;
      }
      const practicePayload = Object.fromEntries(PRACTICE_FIELDS.map((field) => [field, client[field] || null]));
      const { data: updatedClient } = await api.put(`/admin/clients/${id}`, {
        first_name: client.first_name,
        last_name: client.last_name,
        business_name: client.business_name,
        email: client.email,
        autoentry_email: client.autoentry_email,
        sales_autoentry_email: client.sales_autoentry_email || null,
        status: client.status,
        is_vat_client: !!client.is_vat_client,
        ai_analysis_enabled: !!client.ai_analysis_enabled,
        accounting_destination: nextDestination,
        native_accounting_enabled: nextDestination === "native",
        cis_contractor_enabled: !!client.cis_contractor_enabled,
        cis_subcontractor_enabled: !!client.cis_subcontractor_enabled,
        cis_contractor_effective_from: client.cis_contractor_effective_from || null,
        cis_contractor_effective_to: client.cis_contractor_effective_to || null,
        cis_subcontractor_effective_from: client.cis_subcontractor_effective_from || null,
        cis_subcontractor_effective_to: client.cis_subcontractor_effective_to || null,
        ...practicePayload,
      });
      if (nextDestination === "native") updateCachedAccountingClient(id, updatedClient);
      else clearCachedAccountingWorkspace(id);
      setClient(updatedClient);
      window.dispatchEvent(new Event("client-services-updated"));
      toast.success("Client updated");
      if (vatChanged && nextDestination === "native") {
        toast.info(client.is_vat_client
          ? "VAT start date is required before VAT treatment can be applied."
          : "VAT end date is required when disabling VAT for this client.");
        nav(`/admin/accounting?client_id=${encodeURIComponent(id)}&module=vat&tab=Settings`);
        return;
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function deleteClient() {
    try {
      await api.delete(`/admin/clients/${id}`);
      clearCachedAccountingWorkspace(id);
      toast.success("Client deleted");
      nav("/admin");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function resetPassword() {
    if (pwd.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post(`/admin/clients/${id}/reset-password`, { new_password: pwd });
      toast.success("Password reset");
      setPwd("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function searchCompaniesHouse() {
    if (!companyQuery.trim()) return;
    setCompanyBusy(true);
    try {
      const { data } = await api.get("/admin/companies-house/search", { params: { q: companyQuery.trim() } });
      setCompanyResults(data || []);
      if (!data?.length) toast.info("No Companies House matches found");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCompanyBusy(false);
    }
  }

  async function importCompany(companyNumber) {
    setCompanyBusy(true);
    try {
      const { data } = await api.get(`/admin/companies-house/profile/${companyNumber}`);
      setClient((current) => mergeCompanyProfile(current, data));
      setCompanyResults([]);
      toast.success("Companies House details added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCompanyBusy(false);
    }
  }

  async function onUpload(type, file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("type", type);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/clients/${id}/upload-csv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.errors && data.errors.length) {
        const preview = data.errors.slice(0, 8).join("\n");
        toast.error(`Imported ${data.rows_imported} rows, ${data.errors.length} row(s) skipped:\n${preview}${data.errors.length > 8 ? `\n...and ${data.errors.length - 8} more` : ""}`, { duration: 12000 });
        console.warn("CSV row errors:", data.errors);
      } else {
        toast.success(`Imported ${data.rows_imported} rows successfully`);
      }
      await loadOutstandingItems({ force: true });
    } catch (e) {
      toast.error(formatApiError(e), { duration: 10000 });
    }
  }

  function toggleService(service) {
    const current = normaliseServiceSettings(availableServices, client?.service_settings, client?.services_required);
    const nextEnabled = !current[service.key]?.enabled;
    const serviceForDeadline = applyClientServiceSettings(service, current[service.key], client);
    const next = {
      ...current,
      [service.key]: {
        ...(current[service.key] || {}),
        enabled: nextEnabled,
      },
    };
    const enabledLabels = availableServices.filter((item) => next[item.key]?.enabled).map((item) => item.label);
    const existingTasks = parseStoredList(client?.deadline_tasks);
    let nextTasks = existingTasks;

    if (!nextEnabled) {
      nextTasks = existingTasks.filter((task) => task.service !== service.key || task.status === "completed");
    } else if (service.deadline && !existingTasks.some((task) => task.service === service.key && task.status !== "completed")) {
      const windowDates = suggestedDeadlineWindowForService(serviceForDeadline, client);
      let nextDue = windowDates.due_date;
      if (!nextDue && !serviceNeedsClientStartDate(service)) {
        nextDue = window.prompt(`Enter the next deadline for ${service.label} (YYYY-MM-DD or DD/MM/YYYY)`);
      }
      if (nextDue) {
        nextTasks = [...existingTasks, createDeadlineTask(serviceForDeadline, nextDue, nextDue === windowDates.due_date ? windowDates.source : "manual", windowDates.available_from, windowDates.note)];
      }
    }

    setClient((currentClient) => ({
      ...currentClient,
      service_settings: JSON.stringify(next),
      services_required: enabledLabels.join("\n"),
      deadline_tasks: JSON.stringify(nextTasks),
    }));
  }

  function updateServiceFee(serviceKey, fee) {
    const current = normaliseServiceSettings(availableServices, client?.service_settings, client?.services_required);
    const next = {
      ...current,
      [serviceKey]: {
        ...(current[serviceKey] || {}),
        fee,
      },
    };
    updateServices(next);
  }

  function updateServiceStartDate(service, startDate) {
    const current = normaliseServiceSettings(availableServices, client?.service_settings, client?.services_required);
    const next = {
      ...current,
      [service.key]: {
        ...(current[service.key] || {}),
        start_date: startDate,
      },
    };
    const serviceForDeadline = applyClientServiceSettings(service, next[service.key], client);
    const existingTasks = parseStoredList(client?.deadline_tasks);
    let nextTasks = existingTasks;
    if (next[service.key]?.enabled && service.deadline) {
      const windowDates = suggestedDeadlineWindowForService(serviceForDeadline, client);
      if (windowDates.due_date) {
        const replacement = createDeadlineTask(serviceForDeadline, windowDates.due_date, windowDates.source, windowDates.available_from, windowDates.note);
        nextTasks = existingTasks.filter((task) => task.service !== service.key || task.status === "completed");
        nextTasks.push(replacement);
      }
    }
    const enabledLabels = availableServices.filter((item) => next[item.key]?.enabled).map((item) => item.label);
    setClient((currentClient) => ({
      ...currentClient,
      service_settings: JSON.stringify(next),
      services_required: enabledLabels.join("\n"),
      deadline_tasks: JSON.stringify(nextTasks),
    }));
  }

  function updateServices(next) {
    const enabledLabels = availableServices.filter((service) => next[service.key]?.enabled).map((service) => service.label);
    setClient((current) => ({
      ...current,
      service_settings: JSON.stringify(next),
      services_required: enabledLabels.join("\n"),
    }));
  }

  function updateCombinedPricing(key, value) {
    const current = normaliseServiceSettings(availableServices, client?.service_settings, client?.services_required);
    const next = {
      ...current,
      combined: {
        ...(current.combined || {}),
        [key]: value,
      },
    };
    updateServices(next);
  }

  function addManualDeadline(serviceKey, dueDate) {
    const service = availableServices.find((item) => item.key === serviceKey);
    if (!service || !dueDate) return;
    setField("deadline_tasks", JSON.stringify([...deadlineTasks, createDeadlineTask(service, dueDate, "manual")]));
  }

  function completeDeadline(taskId) {
    const nextTasks = deadlineTasks.map((task) => (
      task.id === taskId ? { ...task, status: "completed", completed_at: new Date().toISOString() } : task
    ));
    const completed = deadlineTasks.find((task) => task.id === taskId);
    const nextDue = completed ? nextDueFromTask(completed) : null;
    if (completed && nextDue) {
      const nextAvailable = completed.available_from && completed.recurrence
        ? nextDueFromDate(completed.available_from, completed.recurrence)
        : completed.available_from || "";
      nextTasks.push({
        ...completed,
        id: makeLocalId(),
        due_date: nextDue,
        available_from: nextAvailable,
        status: "open",
        completed_at: null,
        created_at: new Date().toISOString(),
      });
    }
    setField("deadline_tasks", JSON.stringify(nextTasks));
  }

  function selectContactAsMain(contact) {
    const names = splitPersonName(contact?.name);
    setClient((current) => ({
      ...current,
      first_name: names.first_name || current.first_name,
      last_name: names.last_name || current.last_name,
      main_contact_name: contact?.name || current.main_contact_name,
      main_contact_role: contact?.role || contact?.kind || current.main_contact_role,
    }));
    toast.success("Main contact selected");
  }

  async function refreshIntegration() {
    const { data } = await api.get(`/admin/integrations/clients/${id}`);
    setIntegrationDetail(data);
    setIntegrationSettings({ ...DEFAULT_INTEGRATION_SETTINGS, ...(data.integration || {}) });
    setAccountancySoftwareLoaded(true);
  }

  async function persistIntegrationSettings(showToast = true) {
    await api.put(`/admin/integrations/clients/${id}/settings`, integrationSettings);
    if (showToast) toast.success("Accountancy software settings saved");
    await refreshIntegration();
  }

  async function saveIntegrationSettings(e) {
    e.preventDefault();
    setIntegrationBusy(true);
    try {
      await persistIntegrationSettings(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIntegrationBusy(false);
    }
  }

  async function refreshNativeAccounting() {
    setIntegrationBusy(true);
    try {
      const data = await fetchAccountingWorkspace(id, { force: true });
      setNativeAccountingDetail(data);
      toast.success("Native accounting records refreshed");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIntegrationBusy(false);
    }
  }

  async function connectAccountingSoftware() {
    const provider = integrationSettings.provider;
    const providerName = providerLabel(integrationSettings.provider);
    const providerConfig = providerConfigs[provider] || {};
    if (providerConfig.enabled === false) {
      toast.error(`${providerName} integration is disabled globally`);
      return;
    }
    if (!providerConfig.configured) {
      toast.error(`${providerName} app credentials are not configured in Integration Hub`);
      return;
    }
    setIntegrationBusy(true);
    try {
      await persistIntegrationSettings(false);
      const { data } = await api.get(`/admin/integrations/clients/${id}/${provider}/connect`);
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setIntegrationBusy(false);
    }
  }

  async function syncAccountingSoftware() {
    const provider = integrationSettings.provider;
    const providerName = providerLabel(integrationSettings.provider);
    setIntegrationBusy(true);
    try {
      const { data } = await api.post(`/admin/integrations/clients/${id}/${provider}/sync`);
      const counts = data.counts || {};
      const warning = (data.warnings || []).filter(Boolean).join(" ");
      toast.success(`Synced ${providerName}: ${counts.account || 0} accounts, ${counts.supplier || 0} suppliers, ${counts.customer || 0} customers, ${counts.tax_code || 0} VAT codes`);
      if (warning) toast.warning(warning, { duration: 9000 });
      await loadAccountancySoftware({ force: true });
    } catch (e) {
      toast.error(formatApiError(e), { duration: 9000 });
    } finally {
      setIntegrationBusy(false);
    }
  }

  async function deleteIntegrationRecord(recordId) {
    setIntegrationBusy(true);
    try {
      await api.delete(`/admin/integrations/records/${recordId}`);
      toast.success("Synced item removed");
      await loadAccountancySoftware({ force: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIntegrationBusy(false);
    }
  }

  if (!client) return <div className="text-stone-500">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={() => nav("/admin/accounting")} className="gap-2" data-testid="back-to-clients">
          <ArrowLeft className="h-4 w-4" /> Back to clients
        </Button>
        {client.native_accounting_enabled && (
          <Button
            type="button"
            variant="outline"
            onClick={() => nav(`/admin/accounting?client_id=${encodeURIComponent(id)}`)}
            className="gap-2"
            data-testid="back-to-accounting-software"
          >
            <BookOpen className="h-4 w-4" /> Back to accounting software
          </Button>
        )}
      </div>

      <header className="flex flex-col gap-3 rounded-md border border-stone-200 bg-white p-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate font-display text-2xl font-bold text-stone-900">{client.business_name}</h1>
            {client.client_type && <Badge variant="secondary">{labelFor(clientTypeOptions, client.client_type)}</Badge>}
            {client.company_status && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{client.company_status}</Badge>}
          </div>
          <p className="mt-1 text-sm text-stone-600">{client.first_name} {client.last_name} - {client.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={saveClient} className="gap-2" style={{ background: "var(--brand)" }} data-testid="save-client-btn">
            <Save className="h-4 w-4" /> Save
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="delete-client-btn"><Trash2 className="h-4 w-4" /> Delete</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this client?</AlertDialogTitle>
                <AlertDialogDescription>All outstanding items and submissions will be removed. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteClient} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-client">Delete client</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </header>

      {pageTab === "deadlines" ? (
        <div className="space-y-4">
          <section className="flex flex-col gap-3 rounded-md border border-emerald-200 bg-emerald-50/40 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Client workspace</p>
              <h2 className="font-display text-xl font-bold text-stone-900">Deadlines</h2>
              <p className="mt-1 text-sm text-stone-600">Review statutory and service deadlines for this client.</p>
            </div>
            <Button type="button" variant="outline" onClick={() => setPageTab("details")}>Open client settings</Button>
          </section>
          <DeadlinesPanel
            client={client}
            tasks={deadlineTasks}
            services={availableServices}
            setField={setField}
            addManualDeadline={addManualDeadline}
            completeDeadline={completeDeadline}
          />
        </div>
      ) : pageTab === "items" ? (
        <div className="space-y-4">
          <section className="flex flex-col gap-3 rounded-md border border-emerald-200 bg-emerald-50/40 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Client workspace</p>
              <h2 className="font-display text-xl font-bold text-stone-900">Outstanding items</h2>
              <p className="mt-1 text-sm text-stone-600">Manage purchase and sales document requests for this client.</p>
            </div>
            <Button type="button" variant="outline" onClick={() => setPageTab("details")}>Open client settings</Button>
          </section>
          <OutstandingItems items={items} tab={itemsTab} setTab={setItemsTab} onUpload={onUpload} loading={outstandingLoading || !outstandingLoaded} />
        </div>
      ) : (
      <Tabs value={pageTab} onValueChange={setPageTab}>
        <TabsList className="flex h-auto w-full max-w-5xl flex-wrap justify-start gap-1 p-1">
          <TabsTrigger value="details" className="gap-2"><Building2 className="h-4 w-4" /> Account details</TabsTrigger>
          <TabsTrigger value="services" className="gap-2"><CalendarClock className="h-4 w-4" /> Services and pricing</TabsTrigger>
          <TabsTrigger value="deadlines" className="gap-2"><CalendarClock className="h-4 w-4" /> Deadlines</TabsTrigger>
          <TabsTrigger value="items" className="gap-2"><Upload className="h-4 w-4" /> Outstanding items</TabsTrigger>
          {accountancyModuleEnabled && (
            <TabsTrigger value="software" className="gap-2"><PlugZap className="h-4 w-4" /> Accountancy software integration</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="details" className="mt-4 space-y-4">
          <section className="rounded-md border border-stone-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-semibold">Client profile</h2>
                <p className="text-xs text-stone-500">Core contact, practice, and Companies House details.</p>
              </div>
              <span className="rounded-md bg-[var(--brand-soft)] px-2.5 py-1 text-xs font-semibold text-[var(--brand)]">Practice record</span>
            </div>

            <div className="grid gap-3 lg:grid-cols-4">
              <SelectField label="Client type" value={client.client_type || ""} onChange={(v) => setField("client_type", v)} options={clientTypeOptions} />
              <SelectField label="Industry" value={client.industry || ""} onChange={(v) => setField("industry", v)} options={INDUSTRIES.map((v) => [v, v])} />
              <Field label="Practice manager" value={client.practice_manager} onChange={(v) => setField("practice_manager", v)} />
              <SelectField label="Status" value={client.status || "active"} onChange={(v) => setField("status", v)} options={[["active", "Active"], ["inactive", "Inactive"]]} />
            </div>

            <div className="mt-3 grid gap-3 lg:grid-cols-4">
              <Field label="First name" value={client.first_name} onChange={(v) => setField("first_name", v)} testid="edit-first-name" />
              <Field label="Last name" value={client.last_name} onChange={(v) => setField("last_name", v)} testid="edit-last-name" />
              <Field label="Business name" value={client.business_name} onChange={(v) => setField("business_name", v)} testid="edit-business-name" />
              <Field label="Login email" type="email" value={client.email} onChange={(v) => setField("email", v)} testid="edit-email" />
              <Field label="Phone" value={client.phone} onChange={(v) => setField("phone", v)} />
              <Field label="Email for purchase invoices" type="email" value={client.autoentry_email} onChange={(v) => setField("autoentry_email", v)} testid="edit-autoentry-email" />
              <Field label="Email for sales invoices (optional)" type="email" value={client.sales_autoentry_email} onChange={(v) => setField("sales_autoentry_email", v)} testid="edit-sales-autoentry-email" />
              <Field label="Year end" value={client.year_end} onChange={(v) => setField("year_end", v)} placeholder="31/03" />
            </div>

            <div className="mt-3 grid gap-3 lg:grid-cols-3">
              <Toggle label="VAT client" description="AI checks VAT and invoice evidence before email delivery." checked={!!client.is_vat_client} onChange={(checked) => setField("is_vat_client", checked)} testid="edit-vat-client" />
              <Toggle label="AI analysis" description="Client uploads are reviewed before they are emailed." checked={!!client.ai_analysis_enabled} onChange={(checked) => setField("ai_analysis_enabled", checked)} testid="edit-ai-analysis" />
              <PasswordReset pwd={pwd} setPwd={setPwd} resetPassword={resetPassword} />
            </div>
            <div className="mt-4 rounded-md border border-stone-200 bg-stone-50 p-3">
              <h3 className="font-display font-semibold">Construction Industry Scheme</h3>
              <p className="mb-3 text-xs text-stone-500">The two roles are independent. A client may pay subcontractors, work for contractors, or do both.</p>
              <div className="grid gap-3 lg:grid-cols-2">
                <Toggle label="Acts as CIS contractor" description="Pays subcontractors; payments feed the payroll CIS300 return." checked={!!client.cis_contractor_enabled} onChange={(checked) => setField("cis_contractor_enabled", checked)} />
                <Toggle label="Acts as CIS subcontractor" description="Receives deductions from customers; deductions feed the payroll EPS claim for limited companies." checked={!!client.cis_subcontractor_enabled} onChange={(checked) => setField("cis_subcontractor_enabled", checked)} />
                {client.cis_contractor_enabled ? <><Field label="Contractor role starts" type="date" value={client.cis_contractor_effective_from || ""} onChange={(v) => setField("cis_contractor_effective_from", v)} /><Field label="Contractor role ends (optional)" type="date" value={client.cis_contractor_effective_to || ""} onChange={(v) => setField("cis_contractor_effective_to", v)} /></> : null}
                {client.cis_subcontractor_enabled ? <><Field label="Subcontractor role starts" type="date" value={client.cis_subcontractor_effective_from || ""} onChange={(v) => setField("cis_subcontractor_effective_from", v)} /><Field label="Subcontractor role ends (optional)" type="date" value={client.cis_subcontractor_effective_to || ""} onChange={(v) => setField("cis_subcontractor_effective_to", v)} /></> : null}
              </div>
            </div>
          </section>

          <section>
            <CompaniesHousePanel
              client={client}
              setField={setField}
              query={companyQuery}
              setQuery={setCompanyQuery}
              results={companyResults}
              busy={companyBusy}
              search={searchCompaniesHouse}
              importCompany={importCompany}
              directors={directors}
              pscs={pscs}
              contacts={contacts}
              filings={filings}
              selectContactAsMain={selectContactAsMain}
            />

          </section>

          <section className="rounded-md border border-stone-200 bg-white p-4">
            <h2 className="font-display text-lg font-semibold">HMRC and authorisations</h2>
            <div className="mt-3 grid gap-3 lg:grid-cols-4">
              <Field label="UTR" value={client.utr} onChange={(v) => setField("utr", v)} />
              <Field label="VAT number" value={client.vat_number} onChange={(v) => setField("vat_number", v)} />
              <Field label="PAYE reference" value={client.paye_reference} onChange={(v) => setField("paye_reference", v)} />
              <Field label="Accounts office ref" value={client.accounts_office_reference} onChange={(v) => setField("accounts_office_reference", v)} />
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              <SelectField
                label="Default statutory filing route"
                value={client.statutory_submission_route || "external_submission"}
                onChange={(v) => setField("statutory_submission_route", v)}
                options={[
                  ["external_submission", "Filed externally — simulate and retain evidence"],
                  ["direct_submission", "Direct to authority — use the connected filing service"],
                ]}
              />
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                External/manual records a test or separately filed result without transmitting it. Direct submission must save the package internally first and will remain blocked until the relevant HMRC or Companies House transport is configured and certified.
              </div>
            </div>
            <TextAreaField className="mt-3" label="Authorisation codes / access notes" value={client.authorisation_codes} onChange={(v) => setField("authorisation_codes", v)} placeholder="HMRC agent codes, gateway notes, Companies House auth code, client-specific access notes..." />
          </section>
        </TabsContent>

        <TabsContent value="services" className="mt-4 space-y-4">
          <ModuleServicesPricingPanel
            settings={moduleSettings}
            toggleModule={toggleModule}
            updateModuleStartDate={updateModuleStartDate}
          />
          {accountancyModuleEnabled && (
            <AccountancyServicePricing
              services={availableServices}
              settings={serviceSettings}
              selectedServices={selectedServices}
              toggleService={toggleService}
              updateServiceFee={updateServiceFee}
              updateServiceStartDate={updateServiceStartDate}
              updateCombinedPricing={updateCombinedPricing}
              manageCatalogue={() => nav("/admin/accountancy")}
            />
          )}
        </TabsContent>

        {accountancyModuleEnabled && <TabsContent value="software" className="mt-4">
          {accountancySoftwareLoading || !accountancySoftwareLoaded ? (
            <div className="rounded-md border border-stone-200 bg-white p-6 text-sm text-stone-500">Loading accountancy software records...</div>
          ) : (
            <AccountancySoftwarePanel
              client={client}
              setClientField={setField}
              detail={integrationDetail}
              nativeDetail={nativeAccountingDetail}
              settings={integrationSettings}
              setSettings={setIntegrationSettings}
              recordTab={integrationTab}
              setRecordTab={setIntegrationTab}
              providerConfigs={providerConfigs}
              providerConfigsLoading={providerConfigsLoading}
              busy={integrationBusy || accountancySoftwareLoading}
              saveSettings={saveIntegrationSettings}
              refreshNative={refreshNativeAccounting}
              connect={connectAccountingSoftware}
              sync={syncAccountingSoftware}
              deleteRecord={deleteIntegrationRecord}
              loadExternalConfig={loadProviderConfigs}
            />
          )}
        </TabsContent>}
      </Tabs>
      )}
    </div>
  );
}

function ModuleServicesPricingPanel({ settings, toggleModule, updateModuleStartDate }) {
  const enabledCount = CLIENT_MODULES.filter((module) => settings[module.key]?.enabled).length;
  return (
    <section className="rounded-md border border-stone-200 bg-white shadow-sm">
      <div className="flex flex-col gap-2 border-b border-stone-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-[var(--brand)]">Client modules</p>
          <h2 className="font-display text-xl font-semibold text-stone-950">Services and pricing</h2>
          <p className="mt-1 text-sm text-stone-600">Enable only the systems this client uses. Each selection controls access to its complete workflow.</p>
        </div>
        <Badge className="w-fit bg-stone-100 text-stone-700 hover:bg-stone-100">{enabledCount} of 3 enabled</Badge>
      </div>

      <div className="grid gap-4 p-4 lg:grid-cols-3">
        {CLIENT_MODULES.map((module) => {
          const active = !!settings[module.key]?.enabled;
          return (
            <article
              key={module.key}
              className={`flex min-h-[190px] flex-col rounded-xl border-2 p-4 transition ${active ? "border-emerald-500 bg-emerald-50/60 shadow-sm" : "border-stone-200 bg-stone-50/60"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display text-lg font-bold text-stone-950">{module.label}</h3>
                    {module.comingSoon && <Badge variant="secondary" className="text-[10px]">Coming later</Badge>}
                  </div>
                  <p className="mt-2 text-sm leading-5 text-stone-600">{module.description}</p>
                </div>
                <Switch checked={active} onChange={() => toggleModule(module.key)} />
              </div>

              <div className="mt-auto space-y-3 pt-5">
                {module.key === "payroll" && active && (
                  <div>
                    <Label className="text-xs font-semibold text-stone-600">Payroll start date</Label>
                    <Input
                      type="date"
                      value={settings.payroll?.start_date || ""}
                      onChange={(event) => updateModuleStartDate("payroll", event.target.value)}
                      className="mt-1 h-9 bg-white"
                    />
                  </div>
                )}
                <p className={`text-xs font-semibold ${active ? "text-emerald-700" : "text-stone-400"}`}>
                  {active && module.key === "accountancy"
                    ? "Choose the included accountancy services and prices below."
                    : active
                      ? `${module.label} access will be available after saving.`
                      : `${module.label} is not available to this client.`}
                </p>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function AccountancyServicePricing({
  services,
  settings,
  selectedServices,
  toggleService,
  updateServiceFee,
  updateServiceStartDate,
  updateCombinedPricing,
  manageCatalogue,
}) {
  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 rounded-md border border-emerald-200 bg-emerald-50/50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Accountancy module enabled</p>
          <h2 className="font-display text-lg font-semibold text-stone-900">Included accountancy services</h2>
          <p className="mt-1 text-sm text-stone-600">Select the work this client receives and price each service. The accountancy total is calculated from these selections.</p>
        </div>
        <Button type="button" variant="outline" onClick={manageCatalogue}>Manage service catalogue</Button>
      </div>
      <ServicePricingPanel
        services={services}
        combinedPricing={COMBINED_PRICING}
        settings={settings}
        selectedServices={selectedServices}
        toggleService={toggleService}
        updateServiceFee={updateServiceFee}
        updateServiceStartDate={updateServiceStartDate}
        updateCombinedPricing={updateCombinedPricing}
      />
    </section>
  );
}

function ServicePricingPanel({
  services,
  combinedPricing,
  settings,
  selectedServices,
  toggleService,
  updateServiceFee,
  updateServiceStartDate,
  updateCombinedPricing,
}) {
  const serviceTotal = services.reduce((total, service) => {
    const active = !!settings[service.key]?.enabled || selectedServices.includes(service.label);
    if (!active) return total;
    const fee = Number(String(settings[service.key]?.fee || "").replace(/[^0-9.-]/g, ""));
    return total + (Number.isFinite(fee) ? fee : 0);
  }, 0);
  return (
    <div className="rounded-md border border-stone-200 bg-white p-4">
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-[var(--brand)]" />
          <div>
            <h2 className="font-display text-lg font-semibold">Services and pricing</h2>
            <p className="text-xs text-stone-500">Enable services, add fees, and deadline tasks are created only where relevant.</p>
          </div>
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-right">
          <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">Selected service total</div>
          <div className="font-display text-lg font-bold text-stone-950">{serviceTotal.toLocaleString("en-GB", { style: "currency", currency: "GBP" })}</div>
        </div>
      </div>
      <div className="divide-y divide-stone-100 rounded-md border border-stone-200">
        {services.map((service) => {
          const active = !!settings[service.key]?.enabled || selectedServices.includes(service.label);
          return (
            <div key={service.key} className="grid gap-3 px-3 py-2.5 sm:grid-cols-[1fr_84px_160px_180px] sm:items-center">
              <div className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${service.deadline ? "bg-emerald-500" : "bg-stone-300"}`} title={service.deadline ? "Deadline tracked" : "No deadline"} />
                <span className="text-sm font-semibold text-stone-800">{service.label}</span>
                {service.deadline && <Badge variant="secondary" className="text-[10px]">deadline</Badge>}
              </div>
              <Switch checked={active} onChange={() => toggleService(service)} />
              {!isPayrollService(service) && serviceNeedsClientStartDate(service) && active ? (
                <Input
                  type="date"
                  value={settings[service.key]?.start_date || ""}
                  onChange={(e) => updateServiceStartDate(service, e.target.value)}
                  className="h-9"
                  title="First period/start date for deadline calculation"
                />
              ) : (
                <span className="text-xs text-stone-400">{service.deadline && active ? "automatic" : ""}</span>
              )}
              <CurrencyInput value={settings[service.key]?.fee || ""} onChange={(value) => updateServiceFee(service.key, value)} disabled={!active} />
            </div>
          );
        })}
      </div>
      <div className="mt-4">
        <h3 className="mb-2 text-sm font-semibold text-stone-900">Combined pricing</h3>
        <div className="divide-y divide-stone-100 rounded-md border border-stone-200">
          {combinedPricing.map((item) => (
            <div key={item.key} className="grid gap-3 px-3 py-2.5 sm:grid-cols-[1fr_180px] sm:items-center">
              <span className="text-sm font-semibold text-stone-800">{item.label}</span>
              <CurrencyInput value={settings.combined?.[item.key] || ""} onChange={(value) => updateCombinedPricing(item.key, value)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DeadlinesPanel({ client, tasks, services, setField, addManualDeadline, completeDeadline }) {
  const [serviceKey, setServiceKey] = useState(services.find((service) => service.deadline)?.key || "");
  const [dueDate, setDueDate] = useState("");
  const openTasks = tasks.filter((task) => task.status !== "completed").sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
  const completedTasks = tasks.filter((task) => task.status === "completed").slice(-8).reverse();
  const deadlineRows = deadlineDisplayRows(openTasks, client, services);

  return (
    <section className="rounded-md border border-stone-200 bg-white p-4">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Deadline tasks</h2>
            <p className="text-sm text-stone-600">Relevant services create task-style deadlines. Completing one creates the next date when the recurrence is known.</p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{deadlineRows.length} open</Badge>
        </div>

        <div className="grid gap-2 rounded-md border border-stone-200 bg-stone-50 p-3 sm:grid-cols-[1fr_180px_auto]">
          <select value={serviceKey} onChange={(e) => setServiceKey(e.target.value)} className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm">
            {services.filter((service) => service.deadline).map((service) => (
              <option key={service.key} value={service.key}>{service.label}</option>
            ))}
          </select>
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-9 bg-white" />
          <Button type="button" variant="outline" onClick={() => { addManualDeadline(serviceKey, dueDate); setDueDate(""); }}>Add deadline</Button>
        </div>

        <div className="mt-4 space-y-2">
          {deadlineRows.length === 0 ? (
            <p className="rounded-md border border-dashed border-stone-300 py-10 text-center text-sm text-stone-500">No open deadlines yet. Enable a deadline service or add one manually.</p>
          ) : deadlineRows.map((task) => {
            return (
              <div key={task.id} className="grid gap-3 rounded-md border border-stone-200 bg-white px-3 py-3 sm:grid-cols-[1fr_220px_auto] sm:items-center">
                <div>
                  <div className="font-semibold text-stone-900">{task.label}</div>
                  <div className="text-xs text-stone-500">{serviceLabel(task.service, services)}{task.source ? ` - ${task.source}` : ""}</div>
                  {task.calculation_note && <div className="mt-1 text-xs text-stone-600">{task.calculation_note}</div>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-md bg-stone-50 px-2 py-1 text-xs">
                    <div className="font-semibold uppercase tracking-wide text-stone-500">Start</div>
                    <div className="text-stone-800">{task.available_from ? formatDisplayDate(task.available_from) : "-"}</div>
                  </div>
                  <DueBadge date={task.due_date} />
                </div>
                {task.synthetic ? (
                  <Badge variant="secondary" className="justify-center">Calculated</Badge>
                ) : (
                  <Button type="button" size="sm" onClick={() => completeDeadline(task.id)} className="gap-2" style={{ background: "var(--brand)" }}>
                    <CheckCircle2 className="h-4 w-4" /> Complete
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {completedTasks.length > 0 && (
        <div className="rounded-md border border-stone-200 bg-white p-4">
          <h2 className="font-display text-lg font-semibold">Recently completed</h2>
            <div className="mt-3 space-y-2">
              {completedTasks.map((task) => (
                <div key={`${task.id}-done`} className="rounded-md bg-stone-50 px-3 py-2 text-sm">
                  <div className="font-semibold text-stone-800">{task.label}</div>
                  <div className="text-xs text-stone-500">Completed {formatShortDate(task.completed_at)}</div>
                </div>
              ))}
            </div>
        </div>
        )}
    </section>
  );
}

function CompaniesHousePanel({
  client,
  setField,
  query,
  setQuery,
  results,
  busy,
  search,
  importCompany,
  directors,
  pscs,
  contacts,
  filings,
  selectContactAsMain,
}) {
  return (
    <div className="rounded-md border border-stone-200 bg-white p-4">
      <div className="mb-3 flex flex-col justify-between gap-2 sm:flex-row sm:items-start">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-[var(--brand)]" />
          <div>
            <h2 className="font-display text-lg font-semibold">Companies House</h2>
            <p className="text-xs text-stone-500">Lookup fills company details, deadlines, directors, PSCs, and contacts.</p>
          </div>
        </div>
        {client.companies_house_last_checked && (
          <Badge variant="secondary">Checked {formatShortDate(client.companies_house_last_checked)}</Badge>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search company name or number" className="h-9" />
        <Button type="button" variant="outline" onClick={search} disabled={busy} className="gap-2">
          <Search className="h-4 w-4" /> Search
        </Button>
      </div>
      {results.length > 0 && (
        <div className="mt-3 max-h-52 overflow-auto rounded-md border border-stone-200">
          {results.map((company) => (
            <button
              key={company.company_number}
              type="button"
              onClick={() => importCompany(company.company_number)}
              className="flex w-full items-start justify-between gap-3 border-b border-stone-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-stone-50"
            >
              <span>
                <span className="block font-semibold text-stone-900">{company.title}</span>
                <span className="block text-xs text-stone-500">{company.company_number} - {company.address || "No address shown"}</span>
              </span>
              <Badge variant="secondary">{company.company_status}</Badge>
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <Field label="Company number" value={client.company_number} onChange={(v) => setField("company_number", v)} />
        <Field label="Company status" value={client.company_status} onChange={(v) => setField("company_status", v)} />
        <Field label="Incorporation date" value={client.incorporation_date} onChange={(v) => setField("incorporation_date", v)} />
        <Field label="Main contact" value={client.main_contact_name} onChange={(v) => setField("main_contact_name", v)} />
        <Field label="Main contact role" value={client.main_contact_role} onChange={(v) => setField("main_contact_role", v)} />
        <Field label="SIC / industry" value={client.industry} onChange={(v) => setField("industry", v)} />
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <TextAreaField label="Registered office" value={client.registered_office_address} onChange={(v) => setField("registered_office_address", v)} />
        <TextAreaField label="Trading address" value={client.trading_address} onChange={(v) => setField("trading_address", v)} />
      </div>

      <div className="mt-4 grid gap-3 xl:grid-cols-2">
        <ContactTable title="Directors" contacts={directors} onUse={selectContactAsMain} empty="No active directors imported yet." />
        <ContactTable title="PSCs / owners" contacts={pscs} onUse={selectContactAsMain} empty="No active PSCs imported yet." />
      </div>
      {contacts.length > 0 && (
        <div className="mt-3 rounded-md border border-stone-200 bg-stone-50 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">Contact candidates</div>
          <div className="flex flex-wrap gap-2">
            {contacts.slice(0, 12).map((contact, index) => (
              <button
                key={`${contact.name}-${index}`}
                type="button"
                onClick={() => selectContactAsMain(contact)}
                className="rounded-md border border-stone-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-stone-700 hover:border-emerald-300 hover:bg-emerald-50"
              >
                {contact.name} <span className="font-normal text-stone-500">{contact.role || contact.kind}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {filings.length > 0 && (
        <div className="mt-3 overflow-hidden rounded-md border border-stone-200">
          <div className="bg-stone-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-stone-500">Recent filings</div>
          <div className="max-h-56 overflow-auto">
            {filings.map((filing, index) => (
              <div key={`${filing.date}-${filing.type}-${index}`} className="grid gap-2 border-t border-stone-100 px-3 py-2 text-xs sm:grid-cols-[90px_90px_1fr]">
                <span className="font-semibold text-stone-800">{formatDisplayDate(filing.date)}</span>
                <span className="text-stone-500">{filing.type || filing.category}</span>
                <span className="text-stone-700">{humaniseFiling(filing.description)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ContactTable({ title, contacts, onUse, empty }) {
  return (
    <div className="overflow-hidden rounded-md border border-stone-200">
      <div className="bg-stone-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-stone-500">{title}</div>
      {contacts.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-stone-500">{empty}</p>
      ) : (
        <div className="max-h-64 overflow-auto">
          {contacts.map((contact, index) => (
            <div key={`${contact.name}-${index}`} className="border-t border-stone-100 px-3 py-2 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-stone-900">{contact.name}</div>
                  <div className="text-xs text-stone-500">{contact.role || contact.kind || "Contact"}{contact.appointed_on ? ` - appointed ${contact.appointed_on}` : ""}</div>
                  {contact.address && <div className="mt-1 line-clamp-2 text-xs text-stone-500">{contact.address}</div>}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => onUse(contact)}>Main</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OutstandingItems({ items, tab, setTab, onUpload, loading = false }) {
  return (
    <section className="rounded-md border border-stone-200 bg-white p-4">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-lg font-semibold">Outstanding items</h2>
            <p className="text-xs text-stone-500">Upload and manage purchase or sales items separately.</p>
          </div>
          <TabsList>
            <TabsTrigger value="purchase" data-testid="tab-purchase">Purchase ({items.purchase.length})</TabsTrigger>
            <TabsTrigger value="sales" data-testid="tab-sales">Sales ({items.sales.length})</TabsTrigger>
          </TabsList>
        </div>
        {loading && (
          <div className="mb-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-500">
            Loading outstanding items...
          </div>
        )}

        {["purchase", "sales"].map((type) => (
          <TabsContent value={type} key={type} className="space-y-4">
            <CsvUploader type={type} onUpload={onUpload} />
            {items[type].length === 0 ? (
              <p className="py-8 text-center text-sm text-stone-500">No outstanding items. Upload a CSV to get started.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-stone-200">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                    <tr>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items[type].map((item) => (
                      <tr key={item._id} className="border-t border-stone-100" data-testid={`admin-item-${item._id}`}>
                        <td className="px-3 py-2 font-medium text-stone-900">{item.description}</td>
                        <td className="px-3 py-2 text-stone-600">{formatDisplayDate(item.date)}</td>
                        <td className="px-3 py-2 text-stone-700">{item.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}

function AccountancySoftwarePanel({
  client,
  setClientField,
  detail,
  nativeDetail,
  settings,
  setSettings,
  recordTab,
  setRecordTab,
  providerConfigs,
  providerConfigsLoading,
  busy,
  saveSettings,
  refreshNative,
  connect,
  sync,
  deleteRecord,
  loadExternalConfig,
}) {
  const records = detail.records || {};
  const nativeRecords = nativeRecordsFromDetail(nativeDetail);
  const clientId = client?.id || client?._id;
  const providerName = providerLabel(settings.provider);
  const accountOptions = (records.account || []).map((record) => recordLabel(record));
  const taxOptions = (records.tax_code || []).map((record) => recordLabel(record, true));
  const providerConfig = providerConfigs[settings.provider] || {};
  const globallyDisabled = providerConfig.enabled === false;
  const globallyMissing = !providerConfigsLoading && !providerConfig.configured;
  const connectedEnvironment = settings.sandbox ? "sandbox" : "production";
  const accountingDestination = accountingDestinationForClient(client);
  const nativeEnabled = accountingDestination === "native";
  const destinationLocked = accountingDestinationLocked(client);
  const recordTabs = nativeEnabled ? NATIVE_RECORD_TABS : INTEGRATION_RECORD_TABS;
  const visibleRecordTab = recordTabs.some((tab) => tab.key === recordTab) ? recordTab : recordTabs[0].key;
  const [nativeRecordPage, setNativeRecordPage] = useState(1);
  const [nativeRecordPageSize, setNativeRecordPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [nativeRecordPages, setNativeRecordPages] = useState({});
  const [nativeRecordLoading, setNativeRecordLoading] = useState(false);
  const [nativeRecordError, setNativeRecordError] = useState("");
  const nativePaginatedTabs = useMemo(() => new Set(["supplier", "customer"]), []);
  const activeNativePage = nativeRecordPages[visibleRecordTab] || null;
  const activeNativeRows = nativePaginatedTabs.has(visibleRecordTab)
    ? (activeNativePage ? activeNativePage.rows : (nativeRecords[visibleRecordTab] || []))
    : (nativeRecords[visibleRecordTab] || []);
  const activeRecords = nativeEnabled ? activeNativeRows : (records[visibleRecordTab] || []);
  const visibleCounts = nativeEnabled
    ? {
      ...recordCounts(nativeRecords),
      supplier: nativeRecordPages.supplier?.total_rows ?? recordCounts(nativeRecords).supplier ?? 0,
      customer: nativeRecordPages.customer?.total_rows ?? recordCounts(nativeRecords).customer ?? 0,
    }
    : (detail.counts || {});
  const sourceMessage = nativeEnabled
    ? "Using EPOS Native Accounting records."
    : `Synced from ${providerName}. Missing suppliers can be created from invoice review.`;

  function updateSetting(key, value) {
    setSettings((current) => ({ ...current, [key]: value }));
  }

  function selectProvider(value) {
    setSettings((current) => ({
      ...current,
      provider: value,
      status: value === current.provider ? current.status : "not_connected",
      company_id: value === current.provider ? current.company_id : "",
      company_name: value === current.provider ? current.company_name : "",
      sandbox: value === "quickbooks" ? current.sandbox : false,
    }));
  }

  function setAccountingDestination(value) {
    if (destinationLocked) return;
    setClientField("accounting_destination", value);
    setClientField("native_accounting_enabled", value === "native");
  }

  const loadNativeRecordPage = useCallback(async ({ force = false } = {}) => {
    if (!nativeEnabled || !clientId || !nativePaginatedTabs.has(visibleRecordTab)) return;
    void force;
    const endpoint = visibleRecordTab === "supplier" ? "ap/suppliers" : "ar/customers";
    setNativeRecordLoading(true);
    setNativeRecordError("");
    try {
      const { data } = await api.get(`/admin/accounting/clients/${clientId}/${endpoint}`, {
        params: {
          page: nativeRecordPage,
          page_size: nativeRecordPageSize,
        },
      });
      setNativeRecordPages((current) => ({
        ...current,
        [visibleRecordTab]: normalisePaginatedResponse(data, nativeRecordPageSize),
      }));
    } catch (error) {
      setNativeRecordError(formatApiError(error));
    } finally {
      setNativeRecordLoading(false);
    }
  }, [clientId, nativeEnabled, nativePaginatedTabs, nativeRecordPage, nativeRecordPageSize, visibleRecordTab]);

  useEffect(() => {
    setNativeRecordPage(1);
  }, [visibleRecordTab]);

  useEffect(() => {
    loadNativeRecordPage();
  }, [loadNativeRecordPage]);

  async function refreshNativeLists() {
    await refreshNative?.();
    await loadNativeRecordPage({ force: true });
  }

  return (
    <section className="grid gap-4 xl:grid-cols-[0.95fr_1.25fr]">
      <div className="space-y-4">
        <div className="rounded-md border border-stone-200 bg-white p-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-semibold">Accounting destination</h2>
              <p className="mt-1 text-sm text-stone-600">
                Choose where reviewed documents are posted when you publish them.
              </p>
            </div>
            <Badge className={nativeEnabled ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-stone-100 text-stone-700 hover:bg-stone-100"}>
              {nativeEnabled ? "EPOS native" : "External"}
            </Badge>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              disabled={destinationLocked}
              onClick={() => setAccountingDestination("external")}
              className={`rounded-md border p-3 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${!nativeEnabled ? "border-emerald-500 bg-emerald-50 text-emerald-950" : "border-stone-200 bg-stone-50 text-stone-700 hover:border-stone-300"}`}
            >
              <span className="block font-semibold">External software</span>
              <span className="mt-1 block text-xs opacity-80">Connect QuickBooks, Xero, or Sage. Only one package can be active.</span>
            </button>
            <button
              type="button"
              disabled={destinationLocked}
              onClick={() => setAccountingDestination("native")}
              className={`rounded-md border p-3 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${nativeEnabled ? "border-emerald-500 bg-emerald-50 text-emerald-950" : "border-stone-200 bg-stone-50 text-stone-700 hover:border-stone-300"}`}
            >
              <span className="block font-semibold">EPOS native accounting</span>
              <span className="mt-1 block text-xs opacity-80">Post into EPOS ledgers, VAT, suppliers, customers, and reports.</span>
            </button>
          </div>
          {destinationLocked && (
            <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Accounting destination is locked because this client has published accounting transactions.
            </p>
          )}
          {nativeEnabled && !destinationLocked && (
            <p className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              EPOS native accounting will become the active destination after saving.
            </p>
          )}
        </div>

        {nativeEnabled && (
          <details
            className="rounded-md border border-stone-200 bg-white p-4"
            onToggle={(event) => {
              if (event.currentTarget.open) loadExternalConfig?.();
            }}
          >
            <summary className="cursor-pointer text-sm font-semibold text-stone-800">External integrations</summary>
            <div className="mt-3 space-y-3 text-sm text-stone-600">
              <p>
                External software connection settings are available here, but EPOS Native Accounting is the active accounting source for this client.
              </p>
              {providerConfigsLoading && (
                <p className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-stone-500">
                  Loading external integration configuration...
                </p>
              )}
              {settings.status === "connected" && (
                <p className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2">
                  Connected external company: <strong>{settings.company_name || settings.company_id || client.business_name}</strong> ({providerName}, {connectedEnvironment}).
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={sync} disabled={busy || settings.status !== "connected"} className="gap-2">
                  <RefreshCw className="h-4 w-4" /> Sync external lists
                </Button>
                <Button type="button" variant="outline" onClick={connect} disabled={busy || providerConfigsLoading || globallyDisabled || globallyMissing} className="gap-2">
                  <PlugZap className="h-4 w-4" /> {settings.status === "connected" ? "Reconnect" : "Connect"} {providerName}
                </Button>
              </div>
            </div>
          </details>
        )}

        {!nativeEnabled && (
        <form onSubmit={saveSettings} className="rounded-md border border-stone-200 bg-white p-4">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Accountancy software</h2>
            <p className="mt-1 text-sm text-stone-600">
              Connect this client to their accounting package and keep synced accounts, suppliers, customers, and VAT codes on their profile.
            </p>
          </div>
          <Badge className={settings.status === "connected" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-stone-100 text-stone-700 hover:bg-stone-100"}>
            {statusLabel(settings.status)}
          </Badge>
        </div>

        {globallyDisabled && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Accountancy software integration is disabled in Integration Hub.
          </div>
        )}
        {globallyMissing && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {providerName} app credentials are not configured yet. Save and test them in Integration Hub, then connect this client.
          </div>
        )}
        {settings.status === "connected" && (
          <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            Connected accountancy company: <strong>{settings.company_name || settings.company_id || client.business_name}</strong> ({providerName}, {connectedEnvironment}).
          </div>
        )}

        <div className="grid gap-3 lg:grid-cols-2">
          <SelectField
            label="Software"
            value={settings.provider}
            onChange={selectProvider}
            options={INTEGRATION_PROVIDERS.map(({ value, label }) => [value, label])}
          />
          <ReadOnlyIntegrationField label="Connection status" value={statusLabel(settings.status)} />
          <ReadOnlyIntegrationField label="Company / tenant ID" value={settings.company_id || "Assigned during connection"} />
          <ReadOnlyIntegrationField label="Company name" value={settings.company_name || "Assigned during connection"} />
        </div>

        <div className="mt-4 rounded-md border border-stone-200 bg-stone-50 p-3">
          <h3 className="text-sm font-semibold text-stone-900">Default coding</h3>
          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            <IntegrationComboField label="Default purchase account" value={settings.default_purchase_account} onChange={(value) => updateSetting("default_purchase_account", value)} options={accountOptions} />
            <IntegrationComboField label="Default sales account" value={settings.default_sales_account} onChange={(value) => updateSetting("default_sales_account", value)} options={accountOptions} />
            <IntegrationComboField label="Default VAT code" value={settings.default_vat_code} onChange={(value) => updateSetting("default_vat_code", value)} options={taxOptions} />
            <label className="mt-6 flex items-center gap-2 text-sm font-semibold text-stone-700">
              <input type="checkbox" checked={!!settings.sandbox} onChange={(e) => updateSetting("sandbox", e.target.checked)} disabled={settings.status === "connected"} />
              Sandbox company
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Toggle
              label="Create missing suppliers"
              description="Allow invoice review to create supplier records before publishing purchases."
              checked={!!settings.auto_create_suppliers}
              onChange={(checked) => updateSetting("auto_create_suppliers", checked)}
            />
            <Toggle
              label="Create missing customers"
              description="Allow invoice review to create customer records before publishing sales."
              checked={!!settings.auto_create_customers}
              onChange={(checked) => updateSetting("auto_create_customers", checked)}
            />
          </div>
        </div>

        <TextAreaField className="mt-3" label="Notes" value={settings.notes} onChange={(value) => updateSetting("notes", value)} placeholder="Account mapping, VAT behaviour, sync rules, client-specific integration notes..." />

        <div className="mt-4 flex flex-wrap justify-between gap-2">
          <Button type="submit" disabled={busy} className="gap-2" style={{ background: "var(--brand)" }}>
            <Save className="h-4 w-4" /> Save settings
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={sync} disabled={busy || settings.status !== "connected"} className="gap-2">
              <RefreshCw className="h-4 w-4" /> Sync lists
            </Button>
            <Button type="button" onClick={connect} disabled={busy || providerConfigsLoading || globallyDisabled || globallyMissing} className="gap-2" style={{ background: "var(--brand)" }}>
              <PlugZap className="h-4 w-4" /> {settings.status === "connected" ? "Reconnect" : "Connect"} {providerName}
            </Button>
          </div>
        </div>
        </form>
        )}
      </div>

      <div className="rounded-md border border-stone-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-stone-200 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {recordTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = visibleRecordTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    if (nativeEnabled && tab.key !== visibleRecordTab) setNativeRecordPage(1);
                    setRecordTab(tab.key);
                  }}
                  className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${isActive ? "bg-[var(--brand)] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                >
                  <Icon className="h-4 w-4" /> {tab.label}
                  <span className={isActive ? "text-white/80" : "text-stone-500"}>{visibleCounts[tab.key] || 0}</span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="rounded-md bg-stone-50 px-3 py-2 text-sm text-stone-600">{sourceMessage}</p>
            {nativeEnabled && (
              <Button type="button" onClick={refreshNativeLists} disabled={busy || nativeRecordLoading} className="gap-2" style={{ background: "var(--brand)" }}>
                <RefreshCw className="h-4 w-4" /> Refresh native lists
              </Button>
            )}
          </div>
        </div>

        {nativeRecordError && nativeEnabled && nativePaginatedTabs.has(visibleRecordTab) ? (
          <div className="mx-4 mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{nativeRecordError}</div>
        ) : null}
        {nativeRecordLoading && nativeEnabled && nativePaginatedTabs.has(visibleRecordTab) ? (
          <div className="mx-4 mt-4 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-600">Loading {recordTabs.find((tab) => tab.key === visibleRecordTab)?.label || "native records"}...</div>
        ) : null}
        <div className="max-h-[620px] overflow-auto">
          {activeRecords.length === 0 ? (
            <p className="py-24 text-center text-sm text-stone-500">
              {recordTabs.find((tab) => tab.key === visibleRecordTab)?.empty || "No records yet."}
            </p>
          ) : (
            activeRecords.map((record) => (
              <RecordRow key={record.id || record.code || record.name} record={record} source={nativeEnabled ? "native" : "external"} onDelete={deleteRecord} />
            ))
          )}
        </div>
        {nativeEnabled && nativePaginatedTabs.has(visibleRecordTab) ? (
          <PaginationFooter
            page={nativeRecordPage}
            pageSize={nativeRecordPageSize}
            totalRows={activeNativePage?.total_rows ?? activeRecords.length}
            totalPages={activeNativePage?.total_pages}
            disabled={busy || nativeRecordLoading}
            onPageChange={setNativeRecordPage}
            onPageSizeChange={(size) => {
              setNativeRecordPageSize(normalisePageSize(size));
              setNativeRecordPage(1);
            }}
          />
        ) : null}
      </div>
    </section>
  );
}

function RecordRow({ record, source = "external", onDelete }) {
  const name = record.name || record.business_name || record.supplier_name || record.customer_name || record.description || record.code || "Untitled record";
  const description = record.description || record.email || record.contact_email || record.account_type || record.purpose || "No description";
  const code = record.code || record.account_code || record.nominal_code || record.supplier_code || record.customer_code || record.vat_code || "-";
  const isActive = record.active !== false && record.status !== "archived";
  return (
    <div className="grid gap-3 border-b border-stone-100 px-4 py-3 text-sm last:border-b-0 lg:grid-cols-[1fr_160px_180px_auto] lg:items-center">
      <div className="min-w-0">
        <div className="truncate font-semibold text-stone-900">{name}</div>
        <div className="truncate text-stone-500">{description}</div>
        {record.external_id && <div className="mt-1 text-xs text-stone-400">External ID: {record.external_id}</div>}
      </div>
      <div className="truncate text-stone-600">{code}</div>
      <div className="flex gap-2">
        <Badge className={isActive ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-stone-100 text-stone-600 hover:bg-stone-100"}>
          {isActive ? "Active" : "Inactive"}
        </Badge>
        <Badge variant="outline">{source === "native" ? "Native" : "Synced"}</Badge>
      </div>
      {source === "external" && (
        <Button type="button" variant="ghost" size="sm" onClick={() => onDelete(record.id)} className="justify-self-start text-stone-500 hover:text-red-600 lg:justify-self-end">
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function IntegrationComboField({ label, value, onChange, options }) {
  const listId = `${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-options`;
  return (
    <div>
      <Label className="text-xs font-semibold text-stone-600">{label}</Label>
      <Input list={listId} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9" />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </div>
  );
}

function ReadOnlyIntegrationField({ label, value }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-stone-600">{label}</Label>
      <div className="mt-1 flex h-9 items-center rounded-md border border-stone-200 bg-stone-50 px-3 text-sm text-stone-700">
        {value}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <div>
      <HelpLabel label={label} />
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 h-9" data-testid={testid} />
    </div>
  );
}

function TextAreaField({ label, value, onChange, placeholder = "", className = "" }) {
  return (
    <div className={className}>
      <HelpLabel label={label} />
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 min-h-20 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <HelpLabel label={label} />
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm shadow-sm">
        <option value="">Select</option>
        {options.map(([optionValue, labelText]) => (
          <option key={optionValue} value={optionValue}>{labelText}</option>
        ))}
      </select>
    </div>
  );
}

function Toggle({ label, description, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 rounded-md border border-stone-200 bg-stone-50 p-3 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4" data-testid={testid} />
      <span>
        <span className="flex items-center gap-1 font-semibold text-stone-800">{label}<InlineHelp label={label} /></span>
        <span className="block text-xs text-stone-500">{description}</span>
      </span>
    </label>
  );
}

function HelpLabel({ label }) {
  return <Label className="flex items-center gap-1 text-xs font-semibold text-stone-600">{label}<InlineHelp label={label} /></Label>;
}

function InlineHelp({ label }) {
  return (
    <button
      type="button"
      aria-label={`About ${label}`}
      title={helpFor(label)}
      onClick={(event) => { event.preventDefault(); event.stopPropagation(); window.alert(`${label}\n\n${helpFor(label)}`); }}
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-stone-400 hover:bg-emerald-50 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300"
    >
      <HelpCircle className="h-3.5 w-3.5" />
    </button>
  );
}

function Switch({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative h-6 w-11 rounded-full border transition ${checked ? "border-emerald-600 bg-emerald-600" : "border-stone-300 bg-white"}`}
      aria-pressed={checked}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${checked ? "left-5" : "left-0.5"}`} />
    </button>
  );
}

function CurrencyInput({ value, onChange, disabled = false }) {
  return (
    <div className={`flex h-9 overflow-hidden rounded-md border border-stone-200 bg-white shadow-sm ${disabled ? "opacity-50" : ""}`}>
      <span className="flex w-9 items-center justify-center border-r border-stone-200 bg-stone-50 text-sm font-semibold text-stone-500">£</span>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        inputMode="decimal"
        className="min-w-0 flex-1 px-2 text-sm outline-none disabled:bg-white"
      />
    </div>
  );
}

function DueBadge({ date }) {
  const days = daysUntil(date);
  const tone = days < 0 ? "bg-red-100 text-red-800" : days <= 14 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";
  const label = days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "Due today" : `${days}d left`;
  return (
    <div className="flex flex-col items-start gap-1 sm:items-end">
      <span className="font-semibold text-stone-900">{formatDisplayDate(date)}</span>
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{label}</span>
    </div>
  );
}

function PasswordReset({ pwd, setPwd, resetPassword }) {
  return (
    <div className="rounded-md border border-stone-200 bg-stone-50 p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-stone-800"><KeyRound className="h-4 w-4" /> Reset password</div>
      <div className="flex gap-2">
        <Input type="text" placeholder="New password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="h-8" data-testid="new-password-input" />
        <Button onClick={resetPassword} variant="outline" size="sm" data-testid="reset-password-btn">Reset</Button>
      </div>
    </div>
  );
}

function CsvUploader({ type, onUpload }) {
  const inputId = `csv-${type}`;
  return (
    <div className="flex flex-col items-start gap-3 rounded-md border border-dashed border-stone-300 bg-stone-50/70 p-4 sm:flex-row sm:items-center">
      <div className="flex-1">
        <p className="text-sm font-semibold text-stone-800">Upload {type === "purchase" ? "Purchase Invoices" : "Sales Invoices"} CSV</p>
        <p className="mt-1 text-xs text-stone-500">Columns: Description, Date, Amount. Uploading replaces the existing list.</p>
      </div>
      <label htmlFor={inputId} className="cursor-pointer">
        <span className="inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-semibold text-white" style={{ background: "var(--brand)" }} data-testid={`upload-csv-${type}-btn`}>
          <Upload className="h-4 w-4" /> Choose CSV
        </span>
        <input id={inputId} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onUpload(type, e.target.files?.[0])} data-testid={`upload-csv-${type}-input`} />
      </label>
    </div>
  );
}

function splitMulti(value) {
  return String(value || "")
    .split(/\r?\n|,/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function normaliseServiceSettings(services = DEFAULT_SERVICES, value, legacyServices = "") {
  const parsed = parseJsonObject(value);
  const legacy = new Set(splitMulti(legacyServices));
  const result = { ...(parsed || {}) };
  services.forEach((service) => {
    result[service.key] = {
      fee: result[service.key]?.fee || "",
      enabled: !!result[service.key]?.enabled || legacy.has(service.label),
      start_date: result[service.key]?.start_date || service.start_date || "",
    };
  });
  result.combined = {
    annual_charge: result.combined?.annual_charge || "",
    monthly_charge: result.combined?.monthly_charge || "",
  };
  return result;
}

function normaliseModuleSettings(value) {
  const parsed = parseJsonObject(value) || {};
  return {
    accountancy: {
      enabled: !!parsed.accountancy?.enabled,
      fee: parsed.accountancy?.fee || "",
    },
    payroll: {
      enabled: !!parsed.payroll?.enabled,
      fee: parsed.payroll?.fee || "",
      start_date: parsed.payroll?.start_date || "",
    },
    epos_bookings: {
      enabled: !!parsed.epos_bookings?.enabled,
      fee: parsed.epos_bookings?.fee || "",
    },
  };
}

function enabledServiceLabels(services = DEFAULT_SERVICES, serviceSettings, legacyServices = "") {
  const settings = normaliseServiceSettings(services, serviceSettings, legacyServices);
  return services.filter((service) => settings[service.key]?.enabled).map((service) => service.label);
}

function parseJsonObject(value) {
  if (!value) return null;
  if (typeof value === "object" && !Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function parseStoredList(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function createDeadlineTask(service, dueDate, source = "manual", availableFrom = "", calculationNote = "") {
  return {
    id: makeLocalId(),
    service: service.key,
    label: `${service.label} deadline`,
    due_date: normaliseDateInput(dueDate),
    available_from: normaliseDateInput(availableFrom),
    recurrence: service.recurrence || null,
    start_date: service.start_date || null,
    statutory_key: service.statutory_key || null,
    source,
    calculation_note: calculationNote,
    status: "open",
    created_at: new Date().toISOString(),
    completed_at: null,
  };
}

function suggestedDeadlineForService(service, client) {
  return suggestedDeadlineWindowForService(service, client).due_date;
}

function suggestedDeadlineWindowForService(service, client) {
  const keyedWindow = serviceSpecificDeadlineWindow(service, client);
  if (keyedWindow) return keyedWindow;
  if (service.deadline === "statutory" && service.statutory_key) {
    return statutoryWindowForService(service.statutory_key, client, service);
  }
  if (service.recurrence) {
    const dueDate = nextScheduledDate(service);
    return {
      available_from: service.start_date || "",
      due_date: dueDate,
      source: dueDate ? "scheduled" : "manual",
      note: dueDate ? scheduledRuleNote(service) : "",
    };
  }
  return { available_from: "", due_date: "", source: "manual", note: "" };
}

function statutoryDateForService(key, client) {
  return statutoryWindowForService(key, client).due_date;
}

function statutoryWindowForService(key, client, service = {}) {
  const text = client?.statutory_deadlines || "";
  const labels = {
    companies_house_accounts_due: "Accounts due",
    companies_house_accounts_made_up_to: "Accounts next made up to",
    companies_house_confirmation_due: "Confirmation statement due",
    companies_house_confirmation_next_statement: "Confirmation next statement date",
  };
  if (key === "companies_house_accounts_due") {
    const explicitDueDate = extractDeadline(text, "Accounts due");
    const periodEnd = accountsPeriodEndForDeadline(client, explicitDueDate);
    const availableFrom = periodEnd || "";
    const dueDate = explicitDueDate || (periodEnd ? addMonthsToIso(periodEnd, 9) : filingHistoryDeadlineForService(key, client, { allowOfficialDate: false }));
    return {
      available_from: availableFrom,
      due_date: dueDate,
      source: dueDate ? "Companies House" : "manual",
      note: dueDate ? "Uses Companies House accounts due date. Start date is the next accounts made up to/period end." : "",
    };
  }
  if (key === "hmrc_ct600_filing_due") {
    const accountsDue = extractDeadline(text, "Accounts due");
    const periodEnd = accountsPeriodEndForDeadline(client, accountsDue);
    const availableFrom = periodEnd || "";
    return {
      available_from: availableFrom,
      due_date: periodEnd ? addMonthsToIso(periodEnd, 12) : "",
      source: periodEnd ? "HMRC rule" : "manual",
      note: periodEnd ? "CT600 starts from the accounts period end and is due 12 months after that period end." : "",
    };
  }
  if (key === "hmrc_corporation_tax_payment_due") {
    const accountsDue = extractDeadline(text, "Accounts due");
    const periodEnd = accountsPeriodEndForDeadline(client, accountsDue);
    return {
      available_from: periodEnd || "",
      due_date: periodEnd ? addDaysToIso(addMonthsToIso(periodEnd, 9), 1) : "",
      source: periodEnd ? "HMRC rule" : "manual",
      note: periodEnd ? "Corporation Tax payment is normally due 9 months and 1 day after the accounting period end." : "",
    };
  }
  if (key === "companies_house_confirmation_due") {
    const explicitDueDate = extractDeadline(text, "Confirmation statement due");
    const availableFrom = extractDeadline(text, "Confirmation next statement date") || (explicitDueDate ? addDaysToIso(explicitDueDate, -14) : fallbackConfirmationStatementDate(client));
    const dueDate = explicitDueDate || (availableFrom ? addDaysToIso(availableFrom, 14) : filingHistoryDeadlineForService(key, client, { allowOfficialDate: false }));
    return {
      available_from: availableFrom,
      due_date: dueDate,
      source: dueDate ? "Companies House" : "manual",
      note: dueDate ? "Uses Companies House confirmation statement due date. Start date is the next statement date." : "",
    };
  }
  if (key === "hmrc_vat_return_due" || key === "hmrc_vat_payment_due") return vatQuarterWindow(service.start_date);
  if (key === "hmrc_cis_return_due") return currentTaxMonthWindow("CIS", 19, service.start_date);
  if (key === "hmrc_paye_monthly_due") return currentTaxMonthWindow("PAYE/NIC", 22, service.start_date);
  if (key === "hmrc_p11d_due") return currentP11dWindow();
  if (key === "hmrc_self_assessment_due") return currentSelfAssessmentWindow();
  if (key === "hmrc_self_assessment_payment_due") return currentSelfAssessmentWindow("payment");
  if (key === "hmrc_payment_on_account_due") return currentPaymentOnAccountWindow();
  if (labels[key]) {
    const dueDate = extractDeadline(text, labels[key]);
    return { available_from: "", due_date: dueDate, source: dueDate ? "Companies House" : "manual", note: "" };
  }
  return { available_from: "", due_date: "", source: "manual", note: "" };
}

function deadlineDisplayRows(openTasks, client, services) {
  const serviceSettings = normaliseServiceSettings(services, client?.service_settings, client?.services_required);
  const enabledKeys = new Set(services.filter((service) => isServiceEnabled(service, client)).map((service) => service.key));
  const rowsByService = new Map();
  openTasks.forEach((task) => {
    if (!enabledKeys.has(task.service)) return;
    const service = applyClientServiceSettings(services.find((item) => item.key === task.service), serviceSettings[task.service], client);
    const refreshedWindow = suggestedDeadlineWindowForService(service, client);
    rowsByService.set(task.service, {
      ...task,
      available_from: refreshedWindow?.available_from || task.available_from || "",
      due_date: refreshedWindow?.due_date || task.due_date || "",
      source: refreshedWindow?.source || task.source,
      calculation_note: refreshedWindow?.note || task.calculation_note || "",
    });
  });

  services.filter((service) => service.deadline).forEach((rawService) => {
    const serviceKey = rawService.key;
    if (rowsByService.has(serviceKey)) return;
    if (!enabledKeys.has(serviceKey)) return;
    const service = applyClientServiceSettings(rawService, serviceSettings[serviceKey], client);
    const windowDates = suggestedDeadlineWindowForService(service, client);
    if (!windowDates.due_date) return;
    rowsByService.set(serviceKey, {
      id: `calculated_${serviceKey}`,
      service: serviceKey,
      label: `${service.label} deadline`,
      available_from: windowDates.available_from,
      due_date: windowDates.due_date,
      source: windowDates.source,
      calculation_note: windowDates.note,
      synthetic: true,
    });
  });

  return Array.from(rowsByService.values())
    .filter((row) => row.due_date)
    .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
}

function statutoryDeadlineWindows(client) {
  return [
    {
      key: "accounts",
      label: "End year accounts",
      ...statutoryWindowForService("companies_house_accounts_due", client),
      fallbackNote: "Accounts window uses the period end as the preparation start and Companies House due date as the finish date.",
    },
    {
      key: "ct600",
      label: "CT600",
      ...statutoryWindowForService("hmrc_ct600_filing_due", client),
      fallbackNote: "CT600 aligns to the same accounting period end and is normally due 12 months later.",
    },
    {
      key: "confirmation",
      label: "Confirmation statement",
      ...statutoryWindowForService("companies_house_confirmation_due", client),
      fallbackNote: "Confirmation window starts on the statement date and runs to the Companies House due date.",
    },
  ]
    .filter((row) => row.available_from || row.due_date)
    .map((row) => ({ ...row, note: row.note || row.fallbackNote }));
}

function extractDeadline(text, label) {
  const line = String(text || "").split(/\r?\n/).find((item) => item.toLowerCase().startsWith(label.toLowerCase()));
  if (!line) return "";
  const match = line.match(/(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4})/);
  return match ? normaliseDateInput(match[1]) : "";
}

function integratedDeadlineRows(text) {
  return [
    ["Companies House", "Accounts next made up to", extractDeadline(text, "Accounts next made up to")],
    ["Companies House", "Accounts due", extractDeadline(text, "Accounts due")],
    ["Companies House", "Accounts last made up to", extractDeadline(text, "Accounts last made up to")],
    ["Companies House", "Confirmation next statement date", extractDeadline(text, "Confirmation next statement date")],
    ["Companies House", "Confirmation statement due", extractDeadline(text, "Confirmation statement due")],
    ["Companies House", "Confirmation last statement date", extractDeadline(text, "Confirmation last statement date")],
  ]
    .filter(([, , date]) => !!date)
    .map(([source, label, date]) => ({ source, label, date }));
}

function companiesHouseFilingEvidence(client) {
  const filings = parseStoredList(client?.companies_house_filings);
  const rows = [];
  const accounts = latestFilingByKind(filings, "accounts");
  const confirmation = latestFilingByKind(filings, "confirmation");
  if (accounts) {
    rows.push({
      kind: "Accounts filing",
      ...accounts,
      estimated_next_due: filingHistoryDeadlineForService("companies_house_accounts_due", client, { allowOfficialDate: false }),
    });
  }
  if (confirmation) {
    rows.push({
      kind: "Confirmation statement filing",
      ...confirmation,
      estimated_next_due: filingHistoryDeadlineForService("companies_house_confirmation_due", client, { allowOfficialDate: false }),
    });
  }
  return rows;
}

function filingHistoryDeadlineForService(key, client, options = {}) {
  const { allowOfficialDate = true } = options;
  if (allowOfficialDate) {
    const direct = statutoryDateForService(key, client);
    if (direct) return direct;
  }
  const text = client?.statutory_deadlines || "";
  const filings = parseStoredList(client?.companies_house_filings);
  if (key === "companies_house_accounts_due") {
    const nextMadeUpTo = extractDeadline(text, "Accounts next made up to");
    const lastMadeUpTo = extractDeadline(text, "Accounts last made up to");
    if (nextMadeUpTo) return addMonthsToIso(nextMadeUpTo, 9);
    if (lastMadeUpTo) return addMonthsToIso(lastMadeUpTo, 21);
    const filing = latestFilingByKind(filings, "accounts");
    return filing?.date ? addMonthsToIso(filing.date, 21) : "";
  }
  if (key === "companies_house_confirmation_due") {
    const nextStatementDate = extractDeadline(text, "Confirmation next statement date");
    const lastStatementDate = extractDeadline(text, "Confirmation last statement date");
    if (nextStatementDate) return addDaysToIso(nextStatementDate, 14);
    if (lastStatementDate) return addDaysToIso(addMonthsToIso(lastStatementDate, 12), 14);
    const filing = latestFilingByKind(filings, "confirmation");
    return filing?.date ? addDaysToIso(addMonthsToIso(filing.date, 12), 14) : "";
  }
  return "";
}

function accountsPeriodEndForDeadline(client, accountsDue = "") {
  const text = client?.statutory_deadlines || "";
  return extractDeadline(text, "Accounts next made up to") || (accountsDue ? addMonthsToIso(accountsDue, -9) : fallbackAccountsPeriodEnd(client));
}

function fallbackAccountsPeriodEnd(client) {
  const text = client?.statutory_deadlines || "";
  const lastMadeUpTo = extractDeadline(text, "Accounts last made up to");
  if (lastMadeUpTo) return addMonthsToIso(lastMadeUpTo, 12);
  return "";
}

function isServiceEnabled(service, client) {
  const settings = normaliseServiceSettings(DEFAULT_SERVICES, client?.service_settings, client?.services_required);
  if (settings[service.key]?.enabled) return true;
  const current = normaliseServiceSettings([service], client?.service_settings, client?.services_required);
  return !!current[service.key]?.enabled;
}

function fallbackConfirmationStatementDate(client) {
  const text = client?.statutory_deadlines || "";
  const lastStatementDate = extractDeadline(text, "Confirmation last statement date");
  if (lastStatementDate) return addMonthsToIso(lastStatementDate, 12);
  return "";
}

function serviceSpecificDeadlineWindow(service, client) {
  if (!service) return null;
  if (service.key === "p11d") return currentP11dWindow();
  if (service.key === "self_assessment") return currentSelfAssessmentWindow();
  if (service.key === "self_assessment_payment") return currentSelfAssessmentWindow("payment");
  if (service.key === "payment_on_account") return currentPaymentOnAccountWindow();
  if (isPayrollService(service)) return currentTaxMonthWindow("PAYE/NIC", 22, payrollModuleStartDate(client));
  if (service.key === "cis") return currentTaxMonthWindow("CIS", 19, service.start_date);
  if (service.key === "vat_returns") return vatQuarterWindow(service.start_date);
  if (service.key === "corporation_tax_payment") return statutoryWindowForService("hmrc_corporation_tax_payment_due", client, service);
  return null;
}

function applyClientServiceSettings(service, settings = {}, client = {}) {
  if (!service) return {};
  return {
    ...service,
    start_date: isPayrollService(service)
      ? payrollModuleStartDate(client)
      : settings?.start_date || service.start_date || null,
  };
}

function serviceNeedsClientStartDate(service) {
  if (!service) return false;
  if (isPayrollService(service)) return false;
  if (["cis", "vat_returns"].includes(service.key)) return true;
  return service.deadline === "scheduled" && !["p11d", "self_assessment", "self_assessment_payment", "payment_on_account"].includes(service.key);
}

function isPayrollService(service) {
  const key = String(service?.key || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  const label = String(service?.label || "").trim().toLowerCase();
  return key === "payroll" || label === "payroll";
}

function payrollModuleStartDate(client = {}) {
  return normaliseModuleSettings(client?.module_settings).payroll.start_date || "";
}

function currentTaxMonthWindow(label, dueDay, startDate = "") {
  const configuredStart = parseLooseDate(startDate);
  if (!configuredStart) {
    return {
      available_from: "",
      due_date: "",
      source: "manual",
      note: `${label} needs a client start date before monthly deadlines can be calculated.`,
    };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  configuredStart.setHours(0, 0, 0, 0);
  let taxMonthStart = new Date(configuredStart.getFullYear(), configuredStart.getMonth(), 6);
  if (configuredStart.getDate() < 6) taxMonthStart = addMonthsClamped(taxMonthStart, -1, 6);
  let available = addMonthsClamped(taxMonthStart, 1, 6);
  let due = new Date(available.getFullYear(), available.getMonth(), dueDay);
  while (due < today) {
    available = addMonthsClamped(available, 1, 6);
    due = new Date(available.getFullYear(), available.getMonth(), dueDay);
  }
  return {
    available_from: toIsoDate(available),
    due_date: toIsoDate(due),
    source: "HMRC rule",
    note: `${label} window starts after the tax month closes and is due on the ${ordinal(dueDay)} of the following month.`,
  };
}

function currentP11dWindow() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const year = today.getFullYear();
  return {
    available_from: toIsoDate(new Date(year, 3, 6)),
    due_date: toIsoDate(new Date(year, 6, 6)),
    source: "HMRC rule",
    note: "P11D/P11D(b) work starts after the tax year closes on 5 April and is due by 6 July. If already filed, complete the task to move it on.",
  };
}

function currentSelfAssessmentWindow(kind = "return") {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let start = new Date(today.getFullYear(), 3, 6);
  if (today < start) start = new Date(today.getFullYear() - 1, 3, 6);
  let due = new Date(start.getFullYear() + 1, 0, 31);
  if (due < today) {
    start = new Date(start.getFullYear() + 1, 3, 6);
    due = new Date(start.getFullYear() + 1, 0, 31);
  }
  return {
    available_from: toIsoDate(start),
    due_date: toIsoDate(due),
    source: "HMRC rule",
    note: kind === "payment"
      ? "Self Assessment balancing payment and first payment on account are normally due by 31 January after the tax year."
      : "Self Assessment window starts after the tax year ends on 5 April and the online return is due by 31 January.",
  };
}

function currentPaymentOnAccountWindow() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let due = new Date(today.getFullYear(), 6, 31);
  if (due < today) due = new Date(today.getFullYear() + 1, 6, 31);
  return {
    available_from: toIsoDate(new Date(due.getFullYear(), 3, 6)),
    due_date: toIsoDate(due),
    source: "HMRC rule",
    note: "Second Self Assessment payment on account is normally due by 31 July.",
  };
}

function vatQuarterWindow(startDate = "") {
  const start = parseLooseDate(startDate);
  if (!start) {
    return {
      available_from: "",
      due_date: "",
      source: "manual",
      note: "VAT needs the client's first VAT period start date before quarterly deadlines can be calculated.",
    };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  let periodStart = start;
  let periodEnd = addDaysClamped(addMonthsClamped(periodStart, 3, periodStart.getDate()), -1);
  let due = addDaysClamped(addMonthsClamped(periodEnd, 1, periodEnd.getDate()), 7);
  while (due < today) {
    periodStart = addMonthsClamped(periodStart, 3, periodStart.getDate());
    periodEnd = addDaysClamped(addMonthsClamped(periodStart, 3, periodStart.getDate()), -1);
    due = addDaysClamped(addMonthsClamped(periodEnd, 1, periodEnd.getDate()), 7);
  }
  return {
    available_from: toIsoDate(addDaysClamped(periodEnd, 1)),
    due_date: toIsoDate(due),
    source: "HMRC rule",
    note: "Fallback VAT rule: quarterly VAT return/payment is normally due 1 month and 7 days after the VAT period end.",
  };
}

function scheduledRuleNote(service) {
  if (!service?.recurrence) return "";
  return `Scheduled ${service.recurrence.replace("_", "-")} deadline from the service catalogue start date.`;
}

function ordinal(day) {
  const suffix = day % 10 === 1 && day !== 11 ? "st" : day % 10 === 2 && day !== 12 ? "nd" : day % 10 === 3 && day !== 13 ? "rd" : "th";
  return `${day}${suffix}`;
}

function latestFilingByKind(filings, kind) {
  const matches = filings
    .filter((filing) => filingMatchesKind(filing, kind) && parseLooseDate(filing.date))
    .sort((a, b) => parseLooseDate(b.date) - parseLooseDate(a.date));
  return matches[0] || null;
}

function filingMatchesKind(filing, kind) {
  const text = [
    filing?.category,
    filing?.type,
    filing?.description,
  ].join(" ").toLowerCase();
  if (kind === "accounts") {
    return text.includes("accounts") || /^aa/.test(String(filing?.type || "").toLowerCase());
  }
  if (kind === "confirmation") {
    return text.includes("confirmation") || text.includes("statement") || String(filing?.type || "").toLowerCase().startsWith("cs");
  }
  return false;
}

function friendlyFilingDescription(filing) {
  const description = String(filing?.description || "").replaceAll("-", " ").trim();
  const type = filing?.type ? `Type ${filing.type}` : "";
  const category = filing?.category ? filing.category : "";
  return [description, type, category].filter(Boolean).join(" - ") || "Companies House filing";
}

function addMonthsToIso(value, months) {
  const date = parseLooseDate(value);
  if (!date) return "";
  return toIsoDate(addMonthsClamped(date, months, date.getDate()));
}

function addDaysToIso(value, days) {
  const date = parseLooseDate(value);
  if (!date) return "";
  return toIsoDate(addDaysClamped(date, days));
}

function nextDueFromTask(task) {
  const date = parseLooseDate(task.due_date);
  if (!date || !task.recurrence) return null;
  const next = addRecurrence(date, task.recurrence);
  if (!next) return null;
  return toIsoDate(next);
}

function nextDueFromDate(value, recurrence) {
  const date = parseLooseDate(value);
  if (!date || !recurrence) return "";
  const next = addRecurrence(date, recurrence);
  return next ? toIsoDate(next) : "";
}

function nextScheduledDate(service) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!service.recurrence || !service.start_date) return "";
  let next = parseLooseDate(service.start_date);
  if (!next) return "";
  next.setHours(0, 0, 0, 0);
  while (next < today) {
    const following = addRecurrence(next, service.recurrence);
    if (!following) return "";
    next = following;
  }
  return toIsoDate(next);
}

function addRecurrence(date, recurrence) {
  if (!date) return null;
  const anchorDay = date.getDate();
  const next = new Date(date);
  if (recurrence === "weekly") {
    next.setDate(next.getDate() + 7);
    return next;
  }
  if (recurrence === "monthly") return addMonthsClamped(next, 1, anchorDay);
  if (recurrence === "quarterly") return addMonthsClamped(next, 3, anchorDay);
  if (recurrence === "half_year") return addMonthsClamped(next, 6, anchorDay);
  if (recurrence === "annual") return addMonthsClamped(next, 12, anchorDay);
  return null;
}

function addMonthsClamped(date, months, anchorDay) {
  const next = new Date(date);
  next.setDate(1);
  next.setMonth(next.getMonth() + months);
  next.setDate(Math.min(anchorDay, daysInMonth(next.getFullYear(), next.getMonth())));
  return next;
}

function addDaysClamped(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function normaliseDateInput(value) {
  const parsed = parseLooseDate(value);
  return parsed ? toIsoDate(parsed) : String(value || "").trim();
}

function parseLooseDate(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const parsed = new Date(`${text}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) {
    const parsed = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function toIsoDate(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDisplayDate(value) {
  const parsed = parseLooseDate(value);
  if (!parsed) return value || "-";
  return parsed.toLocaleDateString("en-GB");
}

function daysUntil(value) {
  const parsed = parseLooseDate(value);
  if (!parsed) return 9999;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsed.setHours(0, 0, 0, 0);
  return Math.round((parsed - today) / 86400000);
}

function makeLocalId() {
  return `task_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function serviceLabel(key, services = DEFAULT_SERVICES) {
  return services.find((service) => service.key === key)?.label || key;
}

function accountingDestinationForClient(client = {}) {
  client = client || {};
  if (client.accounting_destination) return client.accounting_destination === "epos_native" ? "native" : client.accounting_destination;
  return client.native_accounting_enabled ? "native" : "external";
}

function accountingDestinationLocked(client = {}) {
  client = client || {};
  return !!(client.accounting_destination_locked || client.destination_switching_locked);
}

function destinationChangeMayAffectPublishedDocuments(client = {}) {
  client = client || {};
  return !!(
    client.has_published_accounting_transactions ||
    Number(client.published_accounting_transactions_count || 0) > 0 ||
    Number(client.accounting_transactions_count || 0) > 0 ||
    Number(client.published_documents_count || 0) > 0
  );
}

function nativeRecordsFromDetail(detail = {}) {
  detail = detail || {};
  const accounts = Array.isArray(detail.accounts) ? detail.accounts : [];
  const ap = detail.accounts_payable || {};
  const ar = detail.accounts_receivable || {};
  const vat = detail.vat || {};
  const suppliers = firstArray(ap.suppliers, detail.ap_suppliers, detail.suppliers);
  const customers = firstArray(ar.customers, detail.ar_customers, detail.customers);
  const vatCodes = firstArray(detail.vat_codes, vat.codes, vat.vat_codes);
  const bankAccounts = firstArray(
    detail.bank_accounts,
    accounts.filter((account) => account.purpose === "Bank Account" || account.account_type === "Bank")
  );
  return {
    account: accounts,
    supplier: suppliers,
    customer: customers,
    tax_code: vatCodes,
    bank_account: bankAccounts,
  };
}

function firstArray(...values) {
  return values.find((value) => Array.isArray(value)) || [];
}

function recordCounts(records = {}) {
  return Object.fromEntries(Object.entries(records).map(([key, value]) => [key, Array.isArray(value) ? value.length : 0]));
}

function mergeCompanyProfile(current, data) {
  const imported = Object.fromEntries(Object.entries(data || {}).filter(([, value]) => value != null && value !== ""));
  const next = { ...current, ...imported };
  if (current.first_name || current.last_name) {
    next.first_name = current.first_name;
    next.last_name = current.last_name;
  } else if (imported.main_contact_name) {
    const names = splitPersonName(imported.main_contact_name);
    next.first_name = names.first_name;
    next.last_name = names.last_name;
  }
  if (!current.email) next.email = current.email || "";
  if (!current.autoentry_email) next.autoentry_email = current.autoentry_email || "";
  return next;
}

function splitPersonName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first_name: parts[0] || "", last_name: "" };
  return { first_name: parts.slice(0, -1).join(" "), last_name: parts[parts.length - 1] };
}

function formatShortDate(value) {
  return formatUkDate(value, "");
}

function humaniseFiling(value) {
  return String(value || "").replace(/-/g, " ");
}

function labelFor(options, value) {
  return options.find(([optionValue]) => optionValue === value)?.[1] || value;
}

function frequencyOptions() {
  return [
    ["weekly", "Weekly"],
    ["fortnightly", "Fortnightly"],
    ["monthly", "Monthly"],
    ["quarterly", "Quarterly"],
    ["annual", "Annual"],
    ["ad_hoc", "Ad hoc"],
  ];
}

function providerLabel(value) {
  return INTEGRATION_PROVIDERS.find((provider) => provider.value === value)?.label || "Accountancy software";
}

function statusLabel(value) {
  const labels = {
    connected: "Connected",
    paused: "Paused",
    not_connected: "Not connected",
  };
  return labels[value] || "Not connected";
}

function recordLabel(record, includeCode = false) {
  const parts = [];
  if (includeCode && record.code) parts.push(record.code);
  if (record.name && !parts.includes(record.name)) parts.push(record.name);
  if (record.description && record.description !== record.name) parts.push(record.description);
  return parts.filter(Boolean).join(" - ") || record.external_id || "";
}
